import type { FC, JSX } from 'react';

/**
 * Crystal Guide icon system — canonical CareWeave utility icon set.
 *
 * Visual language: layered translucent fills, palette-driven via CSS custom
 * properties (no currentColor, no raw hex). Amethyst structural base; Citrine
 * (or other warm palette) inner accent only when the icon carries warmth
 * metaphor — flame, sun, "today's entry" celebration dot, sparkle.
 *
 * 40×40 viewBox per icon, sized via `size` prop (default 24).
 *
 * Tokens defined in artifacts/careweave/src/index.css under :root,
 * html.dark-mode, html.high-contrast.
 */

export type CrystalIconKey =
  | 'streak'
  | 'checkin-note'
  | 'medications'
  | 'weather-clear'
  | 'weather-partly'
  | 'weather-cloudy'
  | 'weather-fog'
  | 'weather-drizzle'
  | 'weather-rain'
  | 'weather-snow'
  | 'weather-storm'
  // ─── Weather metric icons (Phase 3 Track D, added 2026-05-24) ───
  // β picks (Vina-approved). Citrine accents on temp + wind use
  // --color-icon-warm-body; amethyst structural parts use currentColor.
  | 'weather-temp'
  | 'weather-wind'
  | 'weather-precip'
  | 'weather-location'
  | 'log-event'
  | 'emotions-body-map'
  | 'hospital-prep'
  | 'crisis-plan'
  | 'journey-trauma'
  | 'journey-sexual'
  | 'journey-hospital'
  | 'journey-transition'
  | 'journey-mental-health'
  | 'growth-sprout'
  | 'journeys'
  | 'guide-unlock-celebration'
  | 'first-meeting'
  | 'new-moment'
  | 'wardrobe-per-guide-update'
  | 'cloud-synced'
  | 'cloud-syncing'
  | 'cloud-offline'
  | 'record-sync-pending'
  | 'downloads-storage'
  | 'tutorial-offline-step'
  | 'journey-clinic-visit'
  | 'journey-er-prep'
  | 'journey-hospital-admission'
  | 'journey-discharge'
  | 'journey-followup'
  | 'journey-ed-recovery'
  | 'journey-pediatric-to-adult'
  | 'journey-school'
  | 'journey-post-secondary'
  | 'journey-work'
  | 'journey-independence'
  | 'journey-adult-transition'
  | 'journey-moving-out'
  | 'journey-anxiety'
  | 'journey-depression'
  | 'journey-trauma-healing'
  | 'journey-substance-use'
  | 'journey-full-picture'
  | 'journey-grief'
  | 'journey-navigating-stigma'
  | 'journey-body-image'
  | 'journey-migration-scd'
  | 'journey-familial-stigma'
  | 'journey-cultural-identity'
  | 'journey-gender-identity'
  | 'journey-sexual-orientation'
  | 'journey-sexual-health'
  | 'journey-menstrual-health'
  | 'journey-family-planning'
  | 'journey-fertility'
  | 'journey-pregnancy-scd'
  | 'journey-pain-management'
  | 'journey-relationships'
  | 'journey-disclosure'
  | 'journey-travel-flying'
  | 'journey-career-workplace'
  | 'journey-financial-insurance'
  | 'journey-accommodation-requests'
  | 'journey-employment-rights'
  // ─── Utility icons (added 2026-05-21 for emoji wire-in pass) ───
  | 'grounding'
  | 'solidarity'
  | 'identity-tag'
  | 'advocacy'
  | 'care-team'
  | 'heart'
  | 'check'
  | 'close'
  | 'warning'
  | 'sparkle'
  // ─── Top-30 batch 1 (added 2026-05-21 evening) ───
  | 'checklist'
  | 'lightbulb'
  | 'shield'
  | 'speech-bubble'
  | 'phone'
  | 'lock'
  // ─── Top-30 batch 2 (added 2026-05-21 evening) ───
  | 'thought-bubble'
  | 'magnifier'
  | 'book'
  | 'chart'
  | 'alert-siren'
  // ─── Top-30 batch 3 (added 2026-05-21 evening) ───
  | 'handshake'
  | 'brain'
  | 'fire'
  | 'stethoscope'
  // ─── Wong-Baker FACES scale (added 2026-05-21 late) ───
  // Citrine-palette face icons drawn for slider-scale readability
  // (22-32px). Cartoon-exaggerated features per pediatric accessibility
  // research: each face must be instantly readable as a distinct distress
  // level for low-literacy + ESL youth.
  | 'faces-0'
  | 'faces-2'
  | 'faces-4'
  | 'faces-6'
  | 'faces-8'
  | 'faces-10'
  // ─── Bottom-nav family (Wave 3.5 Track A, added 2026-05-22) ───
  | 'home'
  | 'tools'
  | 'circle'
  // ─── Mood chip icons (Phase 3 Track C, added 2026-05-24) ───
  // β.1 geometric-abstract family. Use currentColor so chip-pill
  // `color: var(--color-accent)` propagates correctly.
  | 'mood-low'
  | 'mood-anxious'
  | 'mood-calm'
  | 'mood-hopeful'
  | 'mood-warrior'
  | 'mood-lonely'
  // ─── Treatment-rating chip icons (Phase 3 Track B, added 2026-05-24) ───
  // β.1 family recoloured via graduated amethyst (--color-crisis-stage-{1..4}).
  // Consumer sets `color: var(--color-crisis-stage-N)` on the wrapping element.
  | 'treatment-great'
  | 'treatment-good'
  | 'treatment-mixed'
  | 'treatment-poor'
  // ─── Medication frequency icons (Phase 3 Track E, added 2026-05-24) ───
  // β picks: Citrine accents where literal (warmth on sun, day side, alert
  // clapper, "this day" highlight). All-β family — see preview
  // medication-frequency-round-1-preview_v1.html for design rationale.
  | 'med-freq-once-daily'
  | 'med-freq-twice-daily'
  | 'med-freq-thrice-daily'
  | 'med-freq-as-needed'
  | 'med-freq-weekly'
  | 'med-freq-alt-days'
  // ─── Per-level badges built from the logo net (Phase 3 Track G, added 2026-05-24) ───
  // Each level builds the CareWeave woven-net logo up further: more spokes,
  // more arcs, more Crystal Guide dots lit at their true colours. By L7 the
  // full logo is rendered with a Citrine halo. See preview
  // track-g-level-badges-round-5-earlier-crystals-preview_v1.html for the
  // visual story. Crystals enter at L1 (the user's chosen guide is already
  // present from Profile Setup Wizard); first Citrine warmth at L3.
  | 'level-1'
  | 'level-2'
  | 'level-3'
  | 'level-4'
  | 'level-5'
  | 'level-6'
  | 'level-7'
  // ─── Track G utility icons (Phase 3.5, added 2026-05-24) ───
  // Functional / non-identity-loaded icons replacing leftover emoji surfaces
  // (Profile pencil, Crystal Guide Bond gem, age cake, PrivacyPolicy doc +
  // envelope, Tools favourite star pair). Plus the SCD trait sickle, a
  // community-claimed identity marker (crescent shape — what "sickle cell"
  // is literally named for).
  | 'pencil'
  | 'gem'
  | 'cake'
  | 'document'
  | 'envelope'
  | 'star-filled'
  | 'star-outlined'
  | 'trait-sickle';

export interface CrystalIconProps {
  /** Pixel size for both width and height. Defaults to 24. */
  size?: number;
  /** Accessible label. Required unless the icon is purely decorative;
   * in that case the calling component should set aria-hidden on the wrapper. */
  ariaLabel?: string;
  /** Render the empty-state variant when defined for this icon
   * (e.g. Day Streak at 0). Most icons ignore this flag. */
  emptyState?: boolean;
  /** Per-guide tint colour for icons that carry guide-specific identity
   * (e.g. first-meeting). Accepts any CSS colour — hex, var(...), etc.
   * Most icons ignore this. When undefined, the icon falls back to the
   * shared amethyst palette. */
  guideTint?: string;
  /** Optional tint override for the heart icon (mood-vocab variants like
   * 💜 💛 💔 ❤️). Accepts any CSS colour. When undefined, falls back to
   * --color-icon-warm-body. */
  heartTint?: string;
}

// Per-icon implementations follow in subsequent tasks.
// Registry populated in the final scaffold task once all icons are defined.

export const CRYSTAL_ICONS: Record<CrystalIconKey, FC<CrystalIconProps>> = {
  'streak': IconStreak,
  'checkin-note': IconCheckinNote,
  'medications': IconMedications,
  'weather-clear': IconWeatherClear,
  'weather-partly': IconWeatherPartly,
  'weather-cloudy': IconWeatherCloudy,
  'weather-fog': IconWeatherFog,
  'weather-drizzle': IconWeatherDrizzle,
  'weather-rain': IconWeatherRain,
  'weather-snow': IconWeatherSnow,
  'weather-storm': IconWeatherStorm,
  'weather-temp': IconWeatherTemp,
  'weather-wind': IconWeatherWind,
  'weather-precip': IconWeatherPrecip,
  'weather-location': IconWeatherLocation,
  'log-event': IconLogEvent,
  'emotions-body-map': IconEmotionsBodyMap,
  'hospital-prep': IconHospitalPrep,
  'crisis-plan': IconCrisisPlan,
  'journey-trauma': IconJourneyTrauma,
  'journey-sexual': IconJourneySexual,
  'journey-hospital': IconJourneyHospital,
  'journey-transition': IconJourneyTransition,
  'journey-mental-health': IconJourneyMentalHealth,
  'growth-sprout': IconGrowthSprout,
  'journeys': IconJourneys,
  'guide-unlock-celebration': IconGuideUnlockCelebration,
  'first-meeting': IconFirstMeeting,
  'new-moment': IconNewMoment,
  'wardrobe-per-guide-update': IconWardrobePerGuideUpdate,
  'cloud-synced': IconCloudSynced,
  'cloud-syncing': IconCloudSyncing,
  'cloud-offline': IconCloudOffline,
  'record-sync-pending': IconRecordSyncPending,
  'downloads-storage': IconDownloadsStorage,
  'tutorial-offline-step': IconTutorialOfflineStep,
  'journey-clinic-visit': IconJourneyClinicVisit,
  'journey-er-prep': IconJourneyErPrep,
  'journey-hospital-admission': IconJourneyHospitalAdmission,
  'journey-discharge': IconJourneyDischarge,
  'journey-followup': IconJourneyFollowup,
  'journey-ed-recovery': IconJourneyEdRecovery,
  'journey-pediatric-to-adult': IconJourneyPediatricToAdult,
  'journey-school': IconJourneySchool,
  'journey-post-secondary': IconJourneyPostSecondary,
  'journey-work': IconJourneyWork,
  'journey-independence': IconJourneyIndependence,
  'journey-adult-transition': IconJourneyAdultTransition,
  'journey-moving-out': IconJourneyMovingOut,
  'journey-anxiety': IconJourneyAnxiety,
  'journey-depression': IconJourneyDepression,
  'journey-trauma-healing': IconJourneyTraumaHealing,
  'journey-substance-use': IconJourneySubstanceUse,
  'journey-full-picture': IconJourneyFullPicture,
  'journey-grief': IconJourneyGrief,
  'journey-navigating-stigma': IconJourneyNavigatingStigma,
  'journey-body-image': IconJourneyBodyImage,
  'journey-migration-scd': IconJourneyMigrationScd,
  'journey-familial-stigma': IconJourneyFamilialStigma,
  'journey-cultural-identity': IconJourneyCulturalIdentity,
  'journey-gender-identity': IconJourneyGenderIdentity,
  'journey-sexual-orientation': IconJourneySexualOrientation,
  'journey-sexual-health': IconJourneySexualHealth,
  'journey-menstrual-health': IconJourneyMenstrualHealth,
  'journey-family-planning': IconJourneyFamilyPlanning,
  'journey-fertility': IconJourneyFertility,
  'journey-pregnancy-scd': IconJourneyPregnancyScd,
  'journey-pain-management': IconJourneyPainManagement,
  'journey-relationships': IconJourneyRelationships,
  'journey-disclosure': IconJourneyDisclosure,
  'journey-travel-flying': IconJourneyTravelFlying,
  'journey-career-workplace': IconJourneyCareerWorkplace,
  'journey-financial-insurance': IconJourneyFinancialInsurance,
  'journey-accommodation-requests': IconJourneyAccommodationRequests,
  'journey-employment-rights': IconJourneyEmploymentRights,
  'grounding': IconGrounding,
  'solidarity': IconSolidarity,
  'identity-tag': IconIdentityTag,
  'advocacy': IconAdvocacy,
  'care-team': IconCareTeam,
  'heart': IconHeart,
  'check': IconCheck,
  'close': IconClose,
  'warning': IconWarning,
  'sparkle': IconSparkle,
  'checklist': IconChecklist,
  'lightbulb': IconLightbulb,
  'shield': IconShield,
  'speech-bubble': IconSpeechBubble,
  'phone': IconPhone,
  'lock': IconLock,
  'thought-bubble': IconThoughtBubble,
  'magnifier': IconMagnifier,
  'book': IconBook,
  'chart': IconChart,
  'alert-siren': IconAlertSiren,
  'handshake': IconHandshake,
  'brain': IconBrain,
  'fire': IconFire,
  'stethoscope': IconStethoscope,
  'faces-0': IconFaces0,
  'faces-2': IconFaces2,
  'faces-4': IconFaces4,
  'faces-6': IconFaces6,
  'faces-8': IconFaces8,
  'faces-10': IconFaces10,
  'home': IconHome,
  'tools': IconTools,
  'circle': IconCircle,
  'mood-low': IconMoodLow,
  'mood-anxious': IconMoodAnxious,
  'mood-calm': IconMoodCalm,
  'mood-hopeful': IconMoodHopeful,
  'mood-warrior': IconMoodWarrior,
  'mood-lonely': IconMoodLonely,
  'treatment-great': IconTreatmentGreat,
  'treatment-good': IconTreatmentGood,
  'treatment-mixed': IconTreatmentMixed,
  'treatment-poor': IconTreatmentPoor,
  'med-freq-once-daily': IconMedFreqOnceDaily,
  'med-freq-twice-daily': IconMedFreqTwiceDaily,
  'med-freq-thrice-daily': IconMedFreqThriceDaily,
  'med-freq-as-needed': IconMedFreqAsNeeded,
  'med-freq-weekly': IconMedFreqWeekly,
  'med-freq-alt-days': IconMedFreqAltDays,
  'level-1': IconLevel1,
  'level-2': IconLevel2,
  'level-3': IconLevel3,
  'level-4': IconLevel4,
  'level-5': IconLevel5,
  'level-6': IconLevel6,
  'level-7': IconLevel7,
  'pencil': IconPencil,
  'gem': IconGem,
  'cake': IconCake,
  'document': IconDocument,
  'envelope': IconEnvelope,
  'star-filled': IconStarFilled,
  'star-outlined': IconStarOutlined,
  'trait-sickle': IconTraitSickle,
};

/** Shared SVG wrapper. Centralises width/height/viewBox/role/a11y plumbing
 * so individual icons only carry their own <path>/<circle>/<rect> children. */
export function svg(size: number, ariaLabel: string | undefined, children: JSX.Element | JSX.Element[]) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      role="img"
      aria-label={ariaLabel ?? ''}
      aria-hidden={ariaLabel ? undefined : true}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      {children}
    </svg>
  );
}

export function IconStreak({ size = 24, ariaLabel }: CrystalIconProps) {
  // Always renders the full calendar + flame so the icon is recognizable at
  // a glance even on a 0-day streak — the stat number next to it conveys count.
  // The earlier emptyState variant (calendar with a hollow ring, no flame) made
  // the streak surface look empty at 28px and didn't read as "this is the streak
  // icon."
  return svg(size, ariaLabel, <>
    {/* Calendar body */}
    <rect x="4" y="9" width="32" height="26" rx="6" fill="var(--color-icon-body)" />
    {/* Belly inset */}
    <rect x="7" y="17" width="26" height="15" rx="4" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Header band */}
    <path d="M4 15 Q4 9 10 9 L30 9 Q36 9 36 15 L36 17 L4 17 Z" fill="var(--color-icon-accent)" />
    {/* Calendar pegs */}
    <rect x="11" y="3" width="3" height="9" rx="1.5" fill="var(--color-icon-accent)" />
    <rect x="26" y="3" width="3" height="9" rx="1.5" fill="var(--color-icon-accent)" />
    {/* Flame teardrop in Citrine warm */}
    <path d="M20 19 Q26 24 24 29 Q22 32 20 32 Q18 32 16 29 Q14 24 20 19 Z" fill="var(--color-icon-warm-cheek)" />
    <path d="M20 23 Q22.5 26 21.5 28.5 Q20.5 30 20 30 Q19.5 30 18.5 28.5 Q17.5 26 20 23 Z" fill="var(--color-icon-warm-body)" opacity="0.85" />
  </>);
}

export function IconCheckinNote({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Page with folded top-right corner */}
    <path d="M8 5 L26 5 L33 12 L33 35 Q33 36 32 36 L9 36 Q8 36 8 35 Z" fill="var(--color-icon-body)" />
    <path d="M26 5 L33 12 L26 12 Z" fill="var(--color-icon-accent)" />
    <rect x="11" y="16" width="19" height="16" rx="2" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Three writing lines */}
    <line x1="13" y1="20" x2="28" y2="20" stroke="var(--color-icon-cheek)" strokeWidth="1.6" strokeLinecap="round" opacity="0.85" />
    <line x1="13" y1="24" x2="26" y2="24" stroke="var(--color-icon-cheek)" strokeWidth="1.6" strokeLinecap="round" opacity="0.75" />
    <line x1="13" y1="28" x2="24" y2="28" stroke="var(--color-icon-cheek)" strokeWidth="1.6" strokeLinecap="round" opacity="0.65" />
    {/* Today's-entry warm Citrine dot */}
    <circle cx="28" cy="20" r="2.2" fill="var(--color-icon-warm-body)" />
    <circle cx="28" cy="20" r="1" fill="var(--color-icon-warm-cheek)" />
  </>);
}

export function IconMedications({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="rotate(-30 20 20)">
    {/* Lighter (belly) half */}
    <path d="M9 14 L20 14 L20 26 L9 26 Q3 26 3 20 Q3 14 9 14 Z" fill="var(--color-icon-belly)" />
    {/* Darker (body) half */}
    <path d="M20 14 L31 14 Q37 14 37 20 Q37 26 31 26 L20 26 Z" fill="var(--color-icon-body)" />
    {/* Seam shine */}
    <line x1="20" y1="14.5" x2="20" y2="25.5" stroke="var(--color-icon-accent)" strokeWidth="0.6" opacity="0.7" />
    {/* Highlight on the lighter half */}
    <ellipse cx="10.5" cy="17.5" rx="2.5" ry="1.3" fill="var(--color-icon-cheek)" opacity="0.55" />
  </g>);
}

export function IconWeatherClear({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Big Citrine sun, centred. Pure sunshine, no cloud. */}
    <circle cx="20" cy="20" r="7.5" fill="var(--color-icon-warm-body)" />
    <circle cx="20" cy="20" r="4.8" fill="var(--color-icon-warm-cheek)" opacity="0.7" />
    {/* Eight rays radiating evenly at 45° intervals */}
    <line x1="20" y1="4" x2="20" y2="9" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" opacity="0.85" />
    <line x1="31.3" y1="8.7" x2="27.5" y2="12.5" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" opacity="0.85" />
    <line x1="36" y1="20" x2="31" y2="20" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" opacity="0.85" />
    <line x1="31.3" y1="31.3" x2="27.5" y2="27.5" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" opacity="0.85" />
    <line x1="20" y1="36" x2="20" y2="31" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" opacity="0.85" />
    <line x1="8.7" y1="31.3" x2="12.5" y2="27.5" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" opacity="0.85" />
    <line x1="4" y1="20" x2="9" y2="20" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" opacity="0.85" />
    <line x1="8.7" y1="8.7" x2="12.5" y2="12.5" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" opacity="0.85" />
  </>);
}

export function IconWeatherPartly({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Smaller Citrine sun, more cloud cover */}
    <circle cx="28" cy="11" r="4" fill="var(--color-icon-warm-body)" />
    <circle cx="28" cy="11" r="2.4" fill="var(--color-icon-warm-cheek)" opacity="0.7" />
    {/* Sun rays — 3 rays in the upper-right (one fewer than Clear since sun is smaller),
        same outward-push treatment as IconWeatherClear */}
    <line x1="28" y1="2" x2="28" y2="5.5" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" opacity="0.8" />
    <line x1="33.5" y1="4" x2="31" y2="6.5" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" opacity="0.8" />
    <line x1="37" y1="11" x2="33" y2="11" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" opacity="0.8" />
    {/* Cloud — larger than IconWeatherClear, covering more of the sun */}
    <path d="M6 28 Q6 17 14 17 Q16 11 22 13 Q31 13 31 22 Q35 22 35 26 Q35 30 31 30 L10 30 Q6 30 6 28 Z" fill="var(--color-icon-body)" />
    {/* Cloud belly */}
    <ellipse cx="17" cy="24" rx="10" ry="3" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Subtle cloud inner accent */}
    <ellipse cx="13" cy="22" rx="2.5" ry="1.5" fill="var(--color-icon-cheek)" opacity="0.45" />
  </>);
}

export function IconWeatherCloudy({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Upper smaller cloud (lighter — belly token at full opacity) */}
    <path d="M14 14 Q14 7 20 7 Q22 4 27 6 Q33 6 33 12 Q37 12 37 16 Q37 20 33 20 L18 20 Q14 20 14 18 Z" fill="var(--color-icon-belly)" opacity="0.75" />
    {/* Lower main cloud (darker — body token) */}
    <path d="M3 26 Q3 17 12 17 Q14 12 19 14 Q28 14 28 22 Q32 22 32 26 Q32 30 28 30 L7 30 Q3 30 3 28 Z" fill="var(--color-icon-body)" />
    {/* Lower cloud belly highlight */}
    <ellipse cx="14" cy="24" rx="9" ry="3" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Subtle cheek accent on the lower cloud */}
    <ellipse cx="10" cy="22" rx="2.5" ry="1.5" fill="var(--color-icon-cheek)" opacity="0.45" />
  </>);
}

export function IconWeatherFog({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Cloud (smaller and higher than other weather icons to leave room for fog stripes) */}
    <path d="M6 20 Q6 11 14 11 Q16 6 22 8 Q30 8 30 16 Q34 16 34 20 Q34 24 30 24 L10 24 Q6 24 6 22 Z" fill="var(--color-icon-body)" />
    <ellipse cx="16" cy="18" rx="8" ry="2.5" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Fog stripes below the cloud */}
    <line x1="6" y1="28" x2="30" y2="28" stroke="var(--color-icon-belly)" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
    <line x1="10" y1="33" x2="34" y2="33" stroke="var(--color-icon-belly)" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
  </>);
}

export function IconWeatherDrizzle({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Cloud */}
    <path d="M6 22 Q6 13 14 13 Q16 8 22 10 Q30 10 30 18 Q34 18 34 22 Q34 26 30 26 L10 26 Q6 26 6 24 Z" fill="var(--color-icon-body)" />
    <ellipse cx="16" cy="20" rx="8" ry="2.5" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Two small drops */}
    <path d="M14 30 Q14 32 16 32 Q18 32 18 30 L16 28 Z" fill="var(--color-icon-belly)" />
    <path d="M22 32 Q22 34 24 34 Q26 34 26 32 L24 30 Z" fill="var(--color-icon-belly)" />
  </>);
}

export function IconWeatherRain({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Cloud — same proportions as IconWeatherDrizzle */}
    <path d="M6 22 Q6 13 14 13 Q16 8 22 10 Q30 10 30 18 Q34 18 34 22 Q34 26 30 26 L10 26 Q6 26 6 24 Z" fill="var(--color-icon-body)" />
    <ellipse cx="16" cy="20" rx="8" ry="2.5" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Four rain streaks (diagonal, decreasing-opacity not used — uniform body for "real rain") */}
    <line x1="12" y1="29" x2="10" y2="35" stroke="var(--color-icon-body)" strokeWidth="1.8" strokeLinecap="round" opacity="0.8" />
    <line x1="18" y1="29" x2="16" y2="35" stroke="var(--color-icon-body)" strokeWidth="1.8" strokeLinecap="round" opacity="0.8" />
    <line x1="24" y1="29" x2="22" y2="35" stroke="var(--color-icon-body)" strokeWidth="1.8" strokeLinecap="round" opacity="0.8" />
    <line x1="30" y1="29" x2="28" y2="35" stroke="var(--color-icon-body)" strokeWidth="1.8" strokeLinecap="round" opacity="0.8" />
  </>);
}

export function IconWeatherSnow({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Cloud — same as Drizzle/Rain */}
    <path d="M6 22 Q6 13 14 13 Q16 8 22 10 Q30 10 30 18 Q34 18 34 22 Q34 26 30 26 L10 26 Q6 26 6 24 Z" fill="var(--color-icon-body)" />
    <ellipse cx="16" cy="20" rx="8" ry="2.5" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Three snowflakes — each a 3-stroke asterisk in cheek token */}
    <g stroke="var(--color-icon-cheek)" strokeWidth="1.4" strokeLinecap="round" opacity="0.85">
      <g transform="translate(12 32)">
        <line x1="-2.5" y1="0" x2="2.5" y2="0" />
        <line x1="0" y1="-2.5" x2="0" y2="2.5" />
        <line x1="-1.8" y1="-1.8" x2="1.8" y2="1.8" />
      </g>
      <g transform="translate(20 34)">
        <line x1="-2.5" y1="0" x2="2.5" y2="0" />
        <line x1="0" y1="-2.5" x2="0" y2="2.5" />
        <line x1="-1.8" y1="-1.8" x2="1.8" y2="1.8" />
      </g>
      <g transform="translate(28 32)">
        <line x1="-2.5" y1="0" x2="2.5" y2="0" />
        <line x1="0" y1="-2.5" x2="0" y2="2.5" />
        <line x1="-1.8" y1="-1.8" x2="1.8" y2="1.8" />
      </g>
    </g>
  </>);
}

export function IconWeatherStorm({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Cloud — same as Drizzle / Rain / Snow */}
    <path d="M6 22 Q6 13 14 13 Q16 8 22 10 Q30 10 30 18 Q34 18 34 22 Q34 26 30 26 L10 26 Q6 26 6 24 Z" fill="var(--color-icon-body)" />
    <ellipse cx="16" cy="20" rx="8" ry="2.5" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Lightning bolt — Citrine warm cheek fill, warm-body outline (electric warmth focal point) */}
    <path d="M20 27 L15 34 L19 34 L17 38 L24 31 L20 31 L22 27 Z" fill="var(--color-icon-warm-cheek)" stroke="var(--color-icon-warm-body)" strokeWidth="0.5" />
  </>);
}

// ─── Weather metric icons (Phase 3 Track D, added 2026-05-24) ───
// β-direction picks (Vina-approved 2026-05-24, accepting three flagged
// collisions). These ride on WeatherReportScreen's 4-up stats grid and the
// Home widget tail (App.tsx L3153-3154 emoji swaps for 💨 wind / 📍 location).
//
// Color convention:
// - Amethyst structural parts use currentColor (parent sets
//   `color: var(--color-accent)` on the wrapper).
// - Citrine accents use --color-icon-warm-body (canonical Citrine token,
//   matches IconWeatherClear / IconWeatherStorm / IconStreak family).
//
// Source SVG paths are 24×24, wrapped in a scale(1.667) g to fit the
// shared 40×40 viewBox.

export function IconWeatherTemp({ size = 24, ariaLabel }: CrystalIconProps) {
  // β: thermometer outline (amethyst) + Citrine-filled bulb + Citrine mercury
  // stem. Citrine does literal work — measures warmth.
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <rect x="10" y="3" width="4" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" fill="none"/>
    <circle cx="12" cy="18.5" r="2.8" fill="var(--color-icon-warm-body)"/>
    <rect x="11" y="13" width="2" height="5" fill="var(--color-icon-warm-body)"/>
  </g>);
}

export function IconWeatherWind({ size = 24, ariaLabel }: CrystalIconProps) {
  // β: three-stroke breeze; top stroke Citrine (editorial consistency with
  // the warm-accent weather family — flagged that wind isn't literally warm),
  // middle + bottom amethyst.
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <path d="M3 8 H 14 a 2 2 0 1 0 -2 -2" stroke="var(--color-icon-warm-body)" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
    <path d="M3 13 H 18 a 2 2 0 1 1 -2 2" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
    <path d="M3 18 H 11" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" opacity="0.7"/>
  </g>);
}

export function IconWeatherPrecip({ size = 24, ariaLabel }: CrystalIconProps) {
  // β: three filled amethyst drops at staggered scale + opacity, no cloud.
  // Flagged: edges close to mood-Low geometry but distinguishable at chip scale.
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <path d="M8 4 C 6.5 7, 5 9, 5 11 a 3 3 0 0 0 6 0 c 0 -2 -1.5 -4 -3 -7 z" fill="currentColor"/>
    <path d="M16 9 C 15 11, 14 12.5, 14 14 a 2 2 0 0 0 4 0 c 0 -1.5 -1 -3 -2 -5 z" fill="currentColor" opacity="0.6"/>
    <path d="M11 16 C 10.5 17.5, 10 18.5, 10 19.5 a 1.5 1.5 0 0 0 3 0 c 0 -1 -0.5 -2 -1 -3.5 z" fill="currentColor" opacity="0.4"/>
  </g>);
}

export function IconWeatherLocation({ size = 24, ariaLabel }: CrystalIconProps) {
  // β: gem-cut diamond pin outline + amethyst dot core. Flagged: visually
  // echoes Crystal Guide diamond icons; ships as-is per Vina 2026-05-24.
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <path d="M12 3 L 18 9 L 12 21 L 6 9 Z" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinejoin="round"/>
    <circle cx="12" cy="9" r="2" fill="currentColor"/>
  </g>);
}

export function IconLogEvent({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Page silhouette with folded corner (matches IconCheckinNote so they read as a family) */}
    <path d="M8 5 L26 5 L33 12 L33 35 Q33 36 32 36 L9 36 Q8 36 8 35 Z" fill="var(--color-icon-body)" />
    <path d="M26 5 L33 12 L26 12 Z" fill="var(--color-icon-accent)" />
    <rect x="11" y="16" width="19" height="16" rx="2" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Two writing lines (fewer than IconCheckinNote to leave room for the plus) */}
    <line x1="13" y1="20" x2="28" y2="20" stroke="var(--color-icon-cheek)" strokeWidth="1.6" strokeLinecap="round" opacity="0.75" />
    <line x1="13" y1="24" x2="22" y2="24" stroke="var(--color-icon-cheek)" strokeWidth="1.6" strokeLinecap="round" opacity="0.6" />
    {/* Plus sign in bottom-right — the "new entry" affordance, in Citrine warm for visual focus */}
    <line x1="26" y1="27" x2="26" y2="31" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" />
    <line x1="24" y1="29" x2="28" y2="29" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" />
  </>);
}

export function IconEmotionsBodyMap({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Round head */}
    <circle cx="20" cy="10" r="5" fill="var(--color-icon-body)" />
    {/* Simple torso — rounded shoulders, no legs (the figure is a container for emotion, not anatomy) */}
    <path d="M11 18 Q11 16 13 16 L27 16 Q29 16 29 18 L31 34 Q31 36 29 36 L11 36 Q9 36 9 34 Z" fill="var(--color-icon-body)" />
    {/* Belly translucency over torso */}
    <path d="M11 18 Q11 16 13 16 L27 16 Q29 16 29 18 L31 34 Q31 36 29 36 L11 36 Q9 36 9 34 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Heart at chest centre — Citrine warm-body, the focal element */}
    <path d="M20 23 Q17 20 15 22 Q14 25 17 27 L20 30 L23 27 Q26 25 25 22 Q23 20 20 23 Z" fill="var(--color-icon-warm-body)" />
    {/* Heart inner highlight */}
    <path d="M20 26 Q18.5 24.5 17 25.5 Q16.5 27 18 28 L20 29.5 L22 28 Q23.5 27 23 25.5 Q21.5 24.5 20 26 Z" fill="var(--color-icon-warm-cheek)" opacity="0.7" />
  </>);
}

export function IconHospitalPrep({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Bag handle (arched stroke above the bag) */}
    <path d="M14 8 Q14 4 20 4 Q26 4 26 8" stroke="var(--color-icon-accent)" strokeWidth="2.4" fill="none" strokeLinecap="round" />
    {/* Bag body */}
    <rect x="5" y="11" width="30" height="22" rx="4" fill="var(--color-icon-body)" />
    {/* Inner belly inset for depth */}
    <rect x="8" y="14" width="24" height="16" rx="2" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Medical cross on the bag face — Citrine warm-body for warmth of care */}
    <rect x="17" y="16" width="6" height="14" rx="1.5" fill="var(--color-icon-warm-body)" />
    <rect x="13" y="20" width="14" height="6" rx="1.5" fill="var(--color-icon-warm-body)" />
  </>);
}

export function IconCrisisPlan({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Outer body disc */}
    <circle cx="20" cy="20" r="15" fill="var(--color-icon-body)" />
    {/* Inner face — softer belly */}
    <circle cx="20" cy="20" r="11" fill="var(--color-icon-belly)" opacity="0.5" />
    <circle cx="20" cy="20" r="11" stroke="var(--color-icon-accent)" strokeWidth="0.8" fill="none" opacity="0.6" />
    {/* Needle north half — Citrine diamond pointing up */}
    <path d="M20 9 L23 20 L17 20 Z" fill="var(--color-icon-warm-body)" />
    {/* Needle south half — accent diamond pointing down, symmetrical to north */}
    <path d="M20 31 L23 20 L17 20 Z" fill="var(--color-icon-accent)" />
    {/* Centre pivot */}
    <circle cx="20" cy="20" r="1.6" fill="var(--color-icon-accent)" />
    {/* Cardinal tick at top */}
    <line x1="20" y1="5" x2="20" y2="7" stroke="var(--color-icon-accent)" strokeWidth="1.5" strokeLinecap="round" />
  </>);
}

export function IconJourneyTrauma({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Soil mound */}
    <ellipse cx="20" cy="32" rx="14" ry="3" fill="var(--color-icon-body)" />
    <ellipse cx="20" cy="32" rx="11" ry="2" fill="var(--color-icon-belly)" opacity="0.5" />
    {/* Stem rising from soil */}
    <path d="M20 32 Q20 22 20 14" stroke="var(--color-icon-accent)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    {/* Left leaf — amethyst body underneath, Citrine warm-cheek highlight */}
    <path d="M20 22 Q14 19 12 14 Q17 15 20 22 Z" fill="var(--color-icon-body)" />
    <path d="M20 22 Q15.5 19.5 14 16 Q18 17 20 22 Z" fill="var(--color-icon-warm-cheek)" />
    {/* Right leaf */}
    <path d="M20 17 Q26 14 28 9 Q23 10 20 17 Z" fill="var(--color-icon-body)" />
    <path d="M20 17 Q24.5 14.5 26 11 Q22 12 20 17 Z" fill="var(--color-icon-warm-cheek)" />
    {/* New-growth tip — Citrine warm-body dot at apex */}
    <circle cx="20" cy="13" r="1.6" fill="var(--color-icon-warm-body)" />
  </>);
}

export function IconJourneySexual({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Outer amethyst heart silhouette */}
    <path d="M20 34 L18 32 Q9 25 5 19 Q3 12 9 9 Q14 7 20 13 Q26 7 31 9 Q37 12 35 19 Q31 25 22 32 Z" fill="var(--color-icon-body)" />
    {/* Inner Rose Quartz heart — smaller, the tender focal layer */}
    <path d="M20 29 L19 28 Q12 22 9 18 Q8 13 12 11 Q15 10 20 15 Q25 10 28 11 Q32 13 31 18 Q28 22 21 28 Z" fill="var(--color-icon-rose-body)" />
    {/* Innermost Rose Quartz cheek highlight */}
    <path d="M20 25 L19 24 Q14 20 12 17 Q11 13 14 12 Q16 12 20 15 Q24 12 26 12 Q29 13 28 17 Q26 20 21 24 Z" fill="var(--color-icon-rose-cheek)" opacity="0.7" />
  </>);
}

export function IconJourneyHospital({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Building body */}
    <rect x="6" y="10" width="28" height="26" rx="2" fill="var(--color-icon-body)" />
    {/* Roof */}
    <path d="M4 12 L20 4 L36 12 Z" fill="var(--color-icon-accent)" />
    {/* Belly inset on facade */}
    <rect x="9" y="13" width="22" height="20" rx="1" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Citrine medical cross — matches IconHospitalPrep for visual family */}
    <rect x="17" y="18" width="6" height="12" rx="1" fill="var(--color-icon-warm-body)" />
    <rect x="14" y="21" width="12" height="6" rx="1" fill="var(--color-icon-warm-body)" />
  </>);
}

export function IconJourneyTransition({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Two amethyst anchor pillars — pediatric on the left, adult on the right. */}
    <rect x="4" y="22" width="5" height="14" rx="1.5" fill="var(--color-icon-body)" />
    <rect x="31" y="22" width="5" height="14" rx="1.5" fill="var(--color-icon-body)" />
    {/* Bridge arch in Citrine — the connecting span IS the focal element of the icon. */}
    <path d="M4 22 Q20 6 36 22" stroke="var(--color-icon-warm-body)" strokeWidth="3.5" fill="none" strokeLinecap="round" />
    {/* Citrine warm-cheek inner highlight for translucency stack */}
    <path d="M6.5 22 Q20 11 33.5 22" stroke="var(--color-icon-warm-cheek)" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.75" />
  </>);
}

export function IconJourneyMentalHealth({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Left lobe */}
    <path d="M19 6 Q12 6 9 11 Q5 13 6 19 Q4 22 7 26 Q6 31 12 32 Q14 34 19 33 L19 6 Z" fill="var(--color-icon-body)" />
    {/* Right lobe — mirror of left */}
    <path d="M21 6 Q28 6 31 11 Q35 13 34 19 Q36 22 33 26 Q34 31 28 32 Q26 34 21 33 L21 6 Z" fill="var(--color-icon-body)" />
    {/* Centre groove */}
    <line x1="20" y1="6" x2="20" y2="33" stroke="var(--color-icon-accent)" strokeWidth="1" opacity="0.6" />
    {/* Inner folds — small cheek-token curves giving the brain its characteristic texture */}
    <path d="M11 14 Q14 13 16 16" stroke="var(--color-icon-cheek)" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.7" />
    <path d="M29 14 Q26 13 24 16" stroke="var(--color-icon-cheek)" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.7" />
    <path d="M11 24 Q14 23 16 26" stroke="var(--color-icon-cheek)" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.7" />
    <path d="M29 24 Q26 23 24 26" stroke="var(--color-icon-cheek)" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.7" />
  </>);
}

export function IconGrowthSprout({ size = 24, ariaLabel }: CrystalIconProps) {
  // Potted plant — amethyst body, Citrine rim. The earlier "lonely sprout"
  // (single stem + droopy leaf arcs + ground line) read as wilting at small
  // sizes; this redraw anchors the icon with a pot and three upright leaves.
  return svg(size, ariaLabel, <>
    {/* Pot — amethyst trapezoid */}
    <path d="M11 27 L29 27 L27 36 L13 36 Z" fill="var(--color-icon-body)" />
    {/* Pot rim — Citrine warm-body horizontal band on top */}
    <rect x="9" y="25" width="22" height="3" rx="1" fill="var(--color-icon-warm-body)" />
    {/* Left side leaf — deep amethyst, reaching up-and-out */}
    <path d="M20 25 Q14 22 11 14 Q18 17 20 25 Z" fill="var(--color-icon-body)" />
    {/* Right side leaf — mirror */}
    <path d="M20 25 Q26 22 29 14 Q22 17 20 25 Z" fill="var(--color-icon-body)" />
    {/* Centre leaf — lavender amethyst, taller */}
    <path d="M20 25 Q17 17 20 6 Q23 17 20 25 Z" fill="var(--color-icon-cheek)" />
    {/* Centre leaf vein — deeper amethyst-accent highlight */}
    <path d="M20 21 Q18.5 14 20 8 Q21.5 14 20 21 Z" fill="var(--color-icon-accent)" opacity="0.85" />
  </>);
}

export function IconJourneys({ size = 24, ariaLabel }: CrystalIconProps) {
  // Silhouette adapted from svgrepo route-svgrepo-com.svg (24×24 viewBox).
  // Scaled to 40×40 via scale(1.667). Three sub-paths split out so the
  // destination endpoint gets the Citrine warm-body token (warm focal).
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    {/* Start marker — amethyst (where you began) */}
    <path d="M6,2 C7.65685,2 9,3.34315 9,5 C9,6.65685 7.65685,8 6,8 C4.34315,8 3,6.65685 3,5 C3,3.34315 4.34315,2 6,2 Z" fill="var(--color-icon-accent)" />
    {/* Route body — the S-curve connecting start to destination */}
    <path d="M15.5,4 C17.9853,4 20,6.01472 20,8.5 C20,10.9853 17.9853,13 15.5,13 L8.5,13 C7.11929,13 6,14.1193 6,15.5 C6,16.8807 7.11929,18 8.5,18 L13,18 C13.5523,18 14,18.4477 14,19 C14,19.5523 13.5523,20 13,20 L8.5,20 C6.01472,20 4,17.9853 4,15.5 C4,13.0147 6.01472,11 8.5,11 L15.5,11 C16.8807,11 18,9.88071 18,8.5 C18,7.11929 16.8807,6 15.5,6 L11,6 C10.4477,6 10,5.55228 10,5 C10,4.44772 10.4477,4 11,4 L15.5,4 Z" fill="var(--color-icon-body)" />
    {/* Destination — Citrine warm (the focal endpoint, where you're going) */}
    <path d="M18,16 C19.6569,16 21,17.3431 21,19 C21,20.6569 19.6569,22 18,22 C16.3431,22 15,20.6569 15,19 C15,17.3431 16.3431,16 18,16 Z" fill="var(--color-icon-warm-body)" />
  </g>);
}

// ─── Umbrella 8 — per-guide bond surfaces ──────────────────────────────────
// Spec: docs/specs/2026-05-18-umbrella-8-icon-specs.md
// Consumed by Wave 4 GuideUnlockCelebration, GuideLorePage first-meeting,
// Home new-moment card, Profile/Customize wardrobe-update affordance.

export function IconFirstMeeting({ size = 24, ariaLabel, guideTint }: CrystalIconProps) {
  // Generic crystal silhouette (egg-leaning, neutral across the 10 shape vocab).
  // Per-guide identity carried by the tint colour, not the silhouette shape.
  const tint = guideTint ?? 'var(--color-icon-body)';
  return svg(size, ariaLabel, <>
    {/* Crystal silhouette — soft egg form, 18% opacity in guide's palette colour */}
    <path
      d="M20 7 Q28 8 30 18 Q31 26 26 31 Q23 34 20 34 Q17 34 14 31 Q9 26 10 18 Q12 8 20 7 Z"
      fill={tint}
      opacity="0.18"
    />
    {/* Outline at full guide tint for definition without overpowering */}
    <path
      d="M20 7 Q28 8 30 18 Q31 26 26 31 Q23 34 20 34 Q17 34 14 31 Q9 26 10 18 Q12 8 20 7 Z"
      stroke={tint}
      strokeWidth="1.4"
      fill="none"
      opacity="0.7"
    />
    {/* Citrine spark — offset top-right, "first contact" not "centred presence" */}
    <line x1="32" y1="8" x2="32" y2="13" stroke="var(--color-icon-warm-body)" strokeWidth="1.6" strokeLinecap="round" />
    <line x1="29" y1="10.5" x2="34" y2="10.5" stroke="var(--color-icon-warm-body)" strokeWidth="1.6" strokeLinecap="round" />
    <line x1="30" y1="8.5" x2="34" y2="12.5" stroke="var(--color-icon-warm-cheek)" strokeWidth="1.2" strokeLinecap="round" opacity="0.85" />
  </>);
}

export function IconNewMoment({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Open book viewed from above — two facing pages forming a soft V */}
    {/* Left page */}
    <path
      d="M20 14 L9 17 Q8 17.2 8 18 L8 30 Q8 30.5 8.5 30.5 L19.5 27.5 Q20 27.4 20 27 Z"
      fill="var(--color-icon-belly)"
      opacity="var(--color-icon-belly-opacity)"
    />
    <path
      d="M20 14 L9 17 Q8 17.2 8 18 L8 30 Q8 30.5 8.5 30.5 L19.5 27.5 Q20 27.4 20 27 Z"
      stroke="var(--color-icon-accent)"
      strokeWidth="1.6"
      fill="none"
      strokeLinejoin="round"
    />
    {/* Right page */}
    <path
      d="M20 14 L31 17 Q32 17.2 32 18 L32 30 Q32 30.5 31.5 30.5 L20.5 27.5 Q20 27.4 20 27 Z"
      fill="var(--color-icon-belly)"
      opacity="var(--color-icon-belly-opacity)"
    />
    <path
      d="M20 14 L31 17 Q32 17.2 32 18 L32 30 Q32 30.5 31.5 30.5 L20.5 27.5 Q20 27.4 20 27 Z"
      stroke="var(--color-icon-accent)"
      strokeWidth="1.6"
      fill="none"
      strokeLinejoin="round"
    />
    {/* Spine */}
    <line x1="20" y1="14" x2="20" y2="27" stroke="var(--color-icon-accent)" strokeWidth="1.6" strokeLinecap="round" />
    {/* Citrine drop on the right page — the new-moment marker */}
    <circle cx="26.5" cy="22" r="2" fill="var(--color-icon-warm-body)" />
    <circle cx="26.5" cy="22" r="0.9" fill="var(--color-icon-warm-cheek)" />
  </>);
}

export function IconWardrobePerGuideUpdate({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Wardrobe / locker body — rounded top, 18% amethyst fill */}
    <path
      d="M10 12 Q10 8 14 8 L26 8 Q30 8 30 12 L30 34 L10 34 Z"
      fill="var(--color-icon-body)"
      opacity="0.18"
    />
    <path
      d="M10 12 Q10 8 14 8 L26 8 Q30 8 30 12 L30 34 L10 34 Z"
      stroke="var(--color-icon-accent)"
      strokeWidth="1.6"
      fill="none"
      strokeLinejoin="round"
    />
    {/* Hanging rod — subtle */}
    <line x1="12" y1="13" x2="28" y2="13" stroke="var(--color-icon-accent)" strokeWidth="0.9" opacity="0.5" strokeLinecap="round" />
    {/* Three hanging items */}
    <line x1="15" y1="14" x2="15" y2="28" stroke="var(--color-icon-accent)" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="20" y1="14" x2="20" y2="28" stroke="var(--color-icon-accent)" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="25" y1="14" x2="25" y2="28" stroke="var(--color-icon-accent)" strokeWidth="1.8" strokeLinecap="round" />
    {/* Citrine diamond at the top of the middle item — the new-item indicator */}
    <path d="M20 11.5 L22 14 L20 16.5 L18 14 Z" fill="var(--color-icon-warm-body)" />
    <path d="M20 12.6 L21.2 14 L20 15.4 L18.8 14 Z" fill="var(--color-icon-warm-cheek)" />
  </>);
}

export function IconGuideUnlockCelebration({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Top diamond — amethyst stroke, no fill */}
    <path
      d="M20 6 L27 14 L20 22 L13 14 Z"
      stroke="var(--color-icon-accent)"
      strokeWidth="1.6"
      fill="none"
      strokeLinejoin="round"
    />
    {/* Bottom diamond — Citrine warm inset, amethyst stroke. Slightly overlapping at the point. */}
    <path
      d="M20 20 L27 28 L20 36 L13 28 Z"
      fill="var(--color-icon-warm-body)"
      opacity="0.32"
    />
    <path
      d="M20 20 L27 28 L20 36 L13 28 Z"
      stroke="var(--color-icon-accent)"
      strokeWidth="1.6"
      fill="none"
      strokeLinejoin="round"
    />
    {/* Four sparkle dots — diagonal halo, radiating from the meet-point */}
    <circle cx="30" cy="17" r="1.2" fill="var(--color-icon-warm-cheek)" />
    <circle cx="30" cy="25" r="1.2" fill="var(--color-icon-warm-cheek)" />
    <circle cx="10" cy="17" r="1.2" fill="var(--color-icon-warm-cheek)" />
    <circle cx="10" cy="25" r="1.2" fill="var(--color-icon-warm-cheek)" />
  </>);
}

// ─── Umbrella 7.11 — offline-access surfaces ───────────────────────────────
// Spec: docs/specs/2026-05-18-umbrella-7-offline-icon-specs.md
// Tri-state cloud badge + per-record indicator + downloads-storage entry
// + composite tutorial-step icon. Cloud silhouette is shared byte-identically
// across the tri-state so state transitions don't flicker.

/** Shared cloud silhouette — referenced by cloud-synced, cloud-syncing,
 * cloud-offline, and tutorial-offline-step. Do NOT redraw cloud geometry
 * inline in any of those icons; reference this constant. */
const CLOUD_PATH = "M 8 28 Q 5 28 5 23 Q 5 18 10 17 Q 12 11 19 13 Q 27 11 30 17 Q 35 18 35 23 Q 35 28 32 28 Z";

export function IconCloudSynced({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Shared cloud silhouette — amethyst stroke, no fill */}
    <path d={CLOUD_PATH} stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Jade checkmark — elbow centred at (19, 23.5) to compensate for the
     * cloud's bottom-heavy flat edge (geometric centre at 19.5 reads high). */}
    <path d="M15 20.5 L19 23.5 L26 17.5" stroke="var(--color-success)" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </>);
}

export function IconCloudSyncing({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Shared cloud silhouette */}
    <path d={CLOUD_PATH} stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Three citrine dots inside the cloud — middle dot larger, visual rhythm */}
    <circle cx="14.5" cy="22" r="1.4" fill="var(--color-icon-warm-body)" />
    <circle cx="20" cy="22" r="1.8" fill="var(--color-icon-warm-body)" />
    <circle cx="25.5" cy="22" r="1.4" fill="var(--color-icon-warm-body)" />
  </>);
}

export function IconCloudOffline({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Shared cloud silhouette — muted stroke, no fill, no interior */}
    <path d={CLOUD_PATH} stroke="var(--color-text-secondary)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Diagonal slash UR→LL — reads as "offline" without alarmism */}
    <line x1="30" y1="13" x2="10" y2="32" stroke="var(--color-text-secondary)" strokeWidth="2.2" strokeLinecap="round" />
  </>);
}

export function IconRecordSyncPending({ size = 16, ariaLabel }: CrystalIconProps) {
  // Default size 16 per spec — inline list-item badge, smaller than other icons.
  return svg(size, ariaLabel, <>
    {/* Outer amethyst ring */}
    <circle cx="20" cy="20" r="9" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" />
    {/* Inner citrine dot — "this record is waiting" */}
    <circle cx="20" cy="20" r="4" fill="var(--color-icon-warm-body)" />
    <circle cx="20" cy="20" r="2" fill="var(--color-icon-warm-cheek)" />
  </>);
}

export function IconDownloadsStorage({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Folder body — 18% amethyst fill, amethyst stroke */}
    <path
      d="M7 14 L15 14 L18 11 L31 11 Q33 11 33 13 L33 30 Q33 32 31 32 L9 32 Q7 32 7 30 Z"
      fill="var(--color-icon-body)"
      opacity="0.18"
    />
    <path
      d="M7 14 L15 14 L18 11 L31 11 Q33 11 33 13 L33 30 Q33 32 31 32 L9 32 Q7 32 7 30 Z"
      stroke="var(--color-icon-accent)"
      strokeWidth="1.6"
      fill="none"
      strokeLinejoin="round"
    />
    {/* Two stored-document lines inside the folder */}
    <line x1="14" y1="22" x2="26" y2="22" stroke="var(--color-icon-accent)" strokeWidth="1.8" strokeLinecap="round" opacity="0.85" />
    <line x1="14" y1="26" x2="26" y2="26" stroke="var(--color-icon-accent)" strokeWidth="1.8" strokeLinecap="round" opacity="0.7" />
  </>);
}

export function IconTutorialOfflineStep({ size = 24, ariaLabel }: CrystalIconProps) {
  // Composite: cloud (left) + folder (right) at 50% scale each + bidirectional
  // arrow between. Cloud is translated to the left half and scaled to 0.5;
  // folder mirrored to the right half at 0.5.
  return svg(size, ariaLabel, <>
    {/* Left half — cloud, scaled 0.5 around the centre of the cloud bbox */}
    <g transform="translate(-2 6) scale(0.55)">
      <path d={CLOUD_PATH} stroke="var(--color-icon-accent)" strokeWidth="2.6" fill="none" strokeLinejoin="round" />
    </g>
    {/* Right half — folder, scaled 0.55 */}
    <g transform="translate(13 6) scale(0.55)">
      <path
        d="M7 14 L15 14 L18 11 L31 11 Q33 11 33 13 L33 30 Q33 32 31 32 L9 32 Q7 32 7 30 Z"
        stroke="var(--color-icon-accent)"
        strokeWidth="2.6"
        fill="none"
        strokeLinejoin="round"
      />
    </g>
    {/* Bidirectional arrow between cloud and folder */}
    <line x1="18.5" y1="20" x2="21.5" y2="20" stroke="var(--color-icon-accent)" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M18.5 20 L19.8 18.7 M18.5 20 L19.8 21.3" stroke="var(--color-icon-accent)" strokeWidth="1.4" strokeLinecap="round" fill="none" />
    <path d="M21.5 20 L20.2 18.7 M21.5 20 L20.2 21.3" stroke="var(--color-icon-accent)" strokeWidth="1.4" strokeLinecap="round" fill="none" />
  </>);
}

// ─── Wave 2.5 Chunk A — Hospital theme journey icons ──────────────────────
// Spec: docs/plans/2026-05-18-wave-2-5-journey-roster-plan.md (on main)
// Visual cue per spec: building silhouettes, medical-cross accents in
// citrine, doorway/threshold metaphors. Distinct from journey-hospital
// (the generic hospital icon already in the Wave 1 roster).

export function IconJourneyClinicVisit({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Small clinic building — single-storey footprint */}
    <path d="M9 18 L20 11 L31 18 L31 33 L9 33 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M9 18 L20 11 L31 18 L31 33 L9 33 Z" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Door */}
    <path d="M17 33 L17 25 Q17 24 18 24 L22 24 Q23 24 23 25 L23 33" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    {/* Citrine medical cross above the door */}
    <line x1="20" y1="15" x2="20" y2="21" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" />
    <line x1="17" y1="18" x2="23" y2="18" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" />
  </>);
}

export function IconJourneyErPrep({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Hospital-bag silhouette — preparation framing, soft rounded shoulders */}
    <path d="M11 17 L29 17 Q30 17 30 18 L30 33 Q30 34 29 34 L11 34 Q10 34 10 33 L10 18 Q10 17 11 17 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M11 17 L29 17 Q30 17 30 18 L30 33 Q30 34 29 34 L11 34 Q10 34 10 33 L10 18 Q10 17 11 17 Z" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Bag handle */}
    <path d="M15 17 Q15 11 20 11 Q25 11 25 17" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinecap="round" />
    {/* Citrine cross — what's inside, why you're packing it */}
    <line x1="20" y1="22" x2="20" y2="30" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" />
    <line x1="16" y1="26" x2="24" y2="26" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" />
  </>);
}

export function IconJourneyHospitalAdmission({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Larger hospital — two-storey, wider footprint */}
    <rect x="6" y="11" width="28" height="22" rx="2" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <rect x="6" y="11" width="28" height="22" rx="2" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" />
    {/* Floor line */}
    <line x1="6" y1="22" x2="34" y2="22" stroke="var(--color-icon-accent)" strokeWidth="1" opacity="0.5" />
    {/* Open doorway — the admission moment */}
    <path d="M16 33 L16 26 L24 26 L24 33" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="var(--color-icon-warm-body)" fillOpacity="0.25" strokeLinejoin="round" />
    {/* Citrine cross on the upper floor */}
    <line x1="20" y1="14" x2="20" y2="20" stroke="var(--color-icon-warm-body)" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="17" y1="17" x2="23" y2="17" stroke="var(--color-icon-warm-body)" strokeWidth="1.8" strokeLinecap="round" />
  </>);
}

export function IconJourneyDischarge({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Building outline at left half — you're leaving from here */}
    <path d="M5 15 L18 15 L18 33 L5 33 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M5 15 L18 15 L18 33 L5 33 Z" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Doorway (open, you're stepping through) */}
    <path d="M14 33 L14 24 L18 24 L18 33" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    {/* Threshold-out arrow — Citrine, warmth of going home */}
    <line x1="20" y1="24" x2="33" y2="24" stroke="var(--color-icon-warm-body)" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M30 21 L33 24 L30 27" stroke="var(--color-icon-warm-body)" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </>);
}

export function IconJourneyFollowup({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Calendar body */}
    <rect x="7" y="10" width="26" height="22" rx="3" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <rect x="7" y="10" width="26" height="22" rx="3" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" />
    {/* Header band */}
    <path d="M7 16 L33 16" stroke="var(--color-icon-accent)" strokeWidth="1" opacity="0.5" />
    {/* Hanging tabs */}
    <line x1="13" y1="7" x2="13" y2="13" stroke="var(--color-icon-accent)" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="27" y1="7" x2="27" y2="13" stroke="var(--color-icon-accent)" strokeWidth="1.8" strokeLinecap="round" />
    {/* Highlighted return date — Citrine drop */}
    <circle cx="20" cy="24" r="3" fill="var(--color-icon-warm-body)" />
    {/* Check inside the drop */}
    <path d="M18 24 L19.5 25.5 L22 23" stroke="var(--color-icon-warm-cheek)" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </>);
}

export function IconJourneyEdRecovery({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Steam — two wavy Citrine lines rising from the cup */}
    <path d="M16 19 Q13 15 16 11 Q19 8 16 5" stroke="var(--color-icon-warm-body)" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <path d="M24 19 Q27 15 24 11 Q21 8 24 5" stroke="var(--color-icon-warm-body)" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    {/* Mug body */}
    <path d="M12 22 L28 22 L28 32 Q28 34 26 34 L14 34 Q12 34 12 32 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M12 22 L28 22 L28 32 Q28 34 26 34 L14 34 Q12 34 12 32 Z" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Liquid surface — Citrine band at the rim */}
    <rect x="13" y="22.5" width="14" height="2" fill="var(--color-icon-warm-body)" opacity="0.85" />
    {/* Handle */}
    <path d="M28 25 Q34 25 34 28 Q34 31 28 31" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
  </>);
}

// ─── Wave 2.5 Chunk B — Transition + Life theme journey icons ─────────────
// Spec: docs/plans/2026-05-18-wave-2-5-journey-roster-plan.md
// Pediatric-to-adult = clinical care transition (two crystals + arrow).
// Adult-transition = broader life-stage transition (open door + light).
// School = K-12 backpack. Post-secondary = graduation cap.
// Work = briefcase. Independence = key. Moving-out = rolling suitcase.

export function IconJourneyPediatricToAdult({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Smaller crystal (pediatric) — egg silhouette */}
    <path d="M11 17 Q11 12 14 12 Q17 12 17 17 L17 28 Q17 30 15 30 Q14 30 13 30 Q11 30 11 28 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M11 17 Q11 12 14 12 Q17 12 17 17 L17 28 Q17 30 15 30 Q14 30 13 30 Q11 30 11 28 Z" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    {/* Larger crystal (adult) */}
    <path d="M25 13 Q25 9 28 9 Q31 9 31 13 L31 31 Q31 33 29 33 Q28 33 27 33 Q25 33 25 31 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M25 13 Q25 9 28 9 Q31 9 31 13 L31 31 Q31 33 29 33 Q28 33 27 33 Q25 33 25 31 Z" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    {/* Citrine arrow between */}
    <line x1="18" y1="20" x2="24" y2="20" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" />
    <path d="M22 18 L24 20 L22 22" stroke="var(--color-icon-warm-body)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </>);
}

export function IconJourneySchool({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Backpack body */}
    <path d="M11 14 L29 14 Q31 14 31 16 L31 32 Q31 34 29 34 L11 34 Q9 34 9 32 L9 16 Q9 14 11 14 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M11 14 L29 14 Q31 14 31 16 L31 32 Q31 34 29 34 L11 34 Q9 34 9 32 L9 16 Q9 14 11 14 Z" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Two shoulder-strap loops */}
    <path d="M14 14 Q14 8 17 8 Q17 11 17 14" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    <path d="M26 14 Q26 8 23 8 Q23 11 23 14" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    {/* Front pocket — Citrine accent */}
    <rect x="14" y="22" width="12" height="7" rx="1.2" fill="var(--color-icon-warm-body)" opacity="0.3" />
    <rect x="14" y="22" width="12" height="7" rx="1.2" stroke="var(--color-icon-accent)" strokeWidth="1.2" fill="none" />
    {/* Zipper pull */}
    <circle cx="20" cy="25.5" r="0.9" fill="var(--color-icon-warm-body)" />
  </>);
}

export function IconJourneyPostSecondary({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Mortarboard top — diamond / rhombus */}
    <path d="M6 19 L20 13 L34 19 L20 25 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M6 19 L20 13 L34 19 L20 25 Z" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Cap base under the board */}
    <path d="M14 22 L14 27 Q14 30 20 30 Q26 30 26 27 L26 22" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="var(--color-icon-belly)" fillOpacity="0.2" strokeLinejoin="round" />
    {/* Tassel cord + bead — Citrine */}
    <line x1="30" y1="20" x2="30" y2="28" stroke="var(--color-icon-warm-body)" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="30" cy="29" r="1.4" fill="var(--color-icon-warm-body)" />
  </>);
}

export function IconJourneyWork({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Handle */}
    <path d="M15 14 Q15 10 20 10 Q25 10 25 14" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Briefcase body — wide, landscape orientation */}
    <rect x="6" y="14" width="28" height="18" rx="2" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <rect x="6" y="14" width="28" height="18" rx="2" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" />
    {/* Latch seam line */}
    <line x1="6" y1="21" x2="34" y2="21" stroke="var(--color-icon-accent)" strokeWidth="1" opacity="0.5" />
    {/* Citrine latch */}
    <rect x="17" y="20" width="6" height="2" rx="1" fill="var(--color-icon-warm-body)" />
  </>);
}

export function IconJourneyIndependence({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Key bow — Citrine ring (the head of the key, your handle on autonomy) */}
    <circle cx="13" cy="20" r="6" fill="var(--color-icon-warm-body)" opacity="0.25" />
    <circle cx="13" cy="20" r="6" stroke="var(--color-icon-warm-body)" strokeWidth="1.8" fill="none" />
    <circle cx="13" cy="20" r="2.2" stroke="var(--color-icon-warm-body)" strokeWidth="1.4" fill="none" />
    {/* Key shaft */}
    <line x1="19" y1="20" x2="32" y2="20" stroke="var(--color-icon-accent)" strokeWidth="2.2" strokeLinecap="round" />
    {/* Key teeth */}
    <line x1="27" y1="20" x2="27" y2="24" stroke="var(--color-icon-accent)" strokeWidth="2.2" strokeLinecap="round" />
    <line x1="32" y1="20" x2="32" y2="23" stroke="var(--color-icon-accent)" strokeWidth="2.2" strokeLinecap="round" />
  </>);
}

export function IconJourneyAdultTransition({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Doorframe — open door, light beyond */}
    <path d="M11 33 L11 14 Q11 11 14 11 L26 11 Q29 11 29 14 L29 33" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Door opening — Citrine glow showing the room beyond */}
    <path d="M14 33 L14 15 L26 15 L26 33 Z" fill="var(--color-icon-warm-body)" opacity="0.35" />
    <path d="M14 33 L14 15 L26 15 L26 33 Z" stroke="var(--color-icon-accent)" strokeWidth="1.2" fill="none" strokeLinejoin="round" />
    {/* Threshold marker — small Citrine orb at eye level */}
    <circle cx="20" cy="22" r="2" fill="var(--color-icon-warm-body)" />
    <circle cx="20" cy="22" r="0.9" fill="var(--color-icon-warm-cheek)" />
    {/* Floor line */}
    <line x1="6" y1="33" x2="34" y2="33" stroke="var(--color-icon-accent)" strokeWidth="1.4" strokeLinecap="round" />
  </>);
}

export function IconJourneyMovingOut({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Pull handle on top */}
    <path d="M18 8 L22 8 Q23 8 23 9 L23 11 L17 11 L17 9 Q17 8 18 8 Z" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    <line x1="20" y1="11" x2="20" y2="14" stroke="var(--color-icon-accent)" strokeWidth="1.4" strokeLinecap="round" />
    {/* Suitcase body — taller, vertical orientation, distinct from briefcase */}
    <rect x="9" y="14" width="22" height="18" rx="2" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <rect x="9" y="14" width="22" height="18" rx="2" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" />
    {/* Centre seam */}
    <line x1="20" y1="14" x2="20" y2="32" stroke="var(--color-icon-accent)" strokeWidth="1" opacity="0.5" />
    {/* Citrine luggage tag */}
    <rect x="22" y="19" width="5" height="3.5" rx="0.6" fill="var(--color-icon-warm-body)" />
    {/* Wheels — rolling suitcase */}
    <circle cx="13" cy="34" r="1.6" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" />
    <circle cx="27" cy="34" r="1.6" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" />
  </>);
}

// ─── Wave 2.5 Chunk C — Mental health + Trauma journey icons ──────────────
// Spec: brain silhouettes refined per type; water/wave metaphors for grief;
// layered abstract shapes for "full picture." Non-pathologising — the icons
// describe the experience, not the deficit. Brain shape borrowed from
// journey-mental-health (the existing Wave 1 icon) and scaled down so
// add-on motifs (sparkles, droplets) fit cleanly in 40×40.

export function IconJourneyAnxiety({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Brain silhouette — compact, leaving room for jagged sparks */}
    <path d="M19 11 Q14 11 12 14 Q10 15 11 19 Q9 21 11 24 Q11 28 14 29 Q16 30 19 29 L19 11 Z" fill="var(--color-icon-body)" />
    <path d="M21 11 Q26 11 28 14 Q30 15 29 19 Q31 21 29 24 Q29 28 26 29 Q24 30 21 29 L21 11 Z" fill="var(--color-icon-body)" />
    <line x1="20" y1="11" x2="20" y2="29" stroke="var(--color-icon-accent)" strokeWidth="1" opacity="0.6" />
    <path d="M13 16 Q15 15 17 18" stroke="var(--color-icon-cheek)" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.7" />
    <path d="M27 16 Q25 15 23 18" stroke="var(--color-icon-cheek)" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.7" />
    {/* Jagged Citrine sparks — racing thoughts firing outward in all directions */}
    <path d="M4 10 L7 12 L5 14" stroke="var(--color-icon-warm-body)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M36 10 L33 12 L35 14" stroke="var(--color-icon-warm-body)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 30 L7 28 L5 26" stroke="var(--color-icon-warm-body)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M36 30 L33 28 L35 26" stroke="var(--color-icon-warm-body)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18 4 L20 7 L22 4" stroke="var(--color-icon-warm-body)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18 36 L20 33 L22 36" stroke="var(--color-icon-warm-body)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </>);
}

export function IconJourneyDepression({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Damping fog lines above — heaviness pressing down */}
    <line x1="11" y1="9" x2="29" y2="9" stroke="var(--color-icon-accent)" strokeWidth="1.2" strokeLinecap="round" opacity="0.35" />
    <line x1="9" y1="13" x2="31" y2="13" stroke="var(--color-icon-accent)" strokeWidth="1.3" strokeLinecap="round" opacity="0.55" />
    <line x1="7" y1="17" x2="33" y2="17" stroke="var(--color-icon-accent)" strokeWidth="1.4" strokeLinecap="round" opacity="0.75" />
    {/* Citrine sphere — heavy feeling, settled low. Still warm, still whole. */}
    <circle cx="20" cy="27" r="6.5" fill="var(--color-icon-warm-body)" />
    <circle cx="20" cy="27" r="3" fill="var(--color-icon-warm-cheek)" opacity="0.8" />
    {/* Ground line — what holds it */}
    <line x1="6" y1="34" x2="34" y2="34" stroke="var(--color-icon-accent)" strokeWidth="1.4" strokeLinecap="round" opacity="0.7" />
  </>);
}

export function IconJourneyTraumaHealing({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Single broad leaf */}
    <path d="M20 22 Q12 22 9 14 Q12 8 18 10 Q22 14 20 22 Z" fill="var(--color-icon-body)" />
    <path d="M20 22 Q12 22 9 14 Q12 8 18 10 Q22 14 20 22 Z" stroke="var(--color-icon-accent)" strokeWidth="1.2" fill="none" strokeLinejoin="round" />
    {/* Stem down */}
    <path d="M20 22 Q20 28 20 34" stroke="var(--color-icon-accent)" strokeWidth="2" fill="none" strokeLinecap="round" />
    {/* Leaf vein hint */}
    <path d="M20 22 Q15 18 11 13" stroke="var(--color-icon-accent)" strokeWidth="0.8" fill="none" strokeLinecap="round" opacity="0.4" />
    {/* Citrine water droplet ON the leaf — tending, not wounding */}
    <circle cx="15" cy="14" r="2" fill="var(--color-icon-warm-body)" />
    <circle cx="14.5" cy="13.5" r="0.8" fill="var(--color-icon-warm-cheek)" />
  </>);
}

export function IconJourneySubstanceUse({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Tide wave across the icon — patterns over time, neither up nor down */}
    <path d="M5 20 Q8 14 11 20 Q14 26 17 20 Q20 14 23 20 Q26 26 29 20 Q32 14 35 20" stroke="var(--color-icon-accent)" strokeWidth="2.2" fill="none" strokeLinecap="round" />
    {/* Inner softer wave — secondary current */}
    <path d="M5 20 Q8 16 11 20 Q14 24 17 20 Q20 16 23 20 Q26 24 29 20 Q32 16 35 20" stroke="var(--color-icon-belly)" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.6" />
    {/* Citrine marker floating on one peak — the moment of attention */}
    <circle cx="11" cy="14" r="2" fill="var(--color-icon-warm-body)" />
    <circle cx="11" cy="14" r="0.9" fill="var(--color-icon-warm-cheek)" />
  </>);
}

export function IconJourneyFullPicture({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Three overlapping amethyst circles — many sides being seen together */}
    <circle cx="14" cy="15" r="7" stroke="var(--color-icon-accent)" strokeWidth="1.5" fill="var(--color-icon-belly)" fillOpacity="0.2" />
    <circle cx="26" cy="15" r="7" stroke="var(--color-icon-accent)" strokeWidth="1.5" fill="var(--color-icon-belly)" fillOpacity="0.2" />
    <circle cx="20" cy="25" r="7" stroke="var(--color-icon-accent)" strokeWidth="1.5" fill="var(--color-icon-belly)" fillOpacity="0.2" />
    {/* Citrine centre where all three meet */}
    <circle cx="20" cy="20" r="2.5" fill="var(--color-icon-warm-body)" />
    <circle cx="20" cy="20" r="1.2" fill="var(--color-icon-warm-cheek)" />
  </>);
}

export function IconJourneyGrief({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Citrine teardrop above — grief has a source */}
    <path d="M20 8 Q24 14 22 18 Q20 20 18 18 Q16 14 20 8 Z" fill="var(--color-icon-warm-body)" />
    <path d="M20 12 Q22 15 21 17 Q20 18 19 17 Q18 15 20 12 Z" fill="var(--color-icon-warm-cheek)" opacity="0.8" />
    {/* Amethyst ripples expanding outward — grief moves */}
    <ellipse cx="20" cy="24" rx="6" ry="1.5" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" />
    <ellipse cx="20" cy="28" rx="9" ry="1.8" stroke="var(--color-icon-accent)" strokeWidth="1.2" fill="none" opacity="0.7" />
    <ellipse cx="20" cy="32" rx="12" ry="2" stroke="var(--color-icon-accent)" strokeWidth="1" fill="none" opacity="0.5" />
  </>);
}

// ─── Wave 2.5 Chunk D — Identity / wellbeing journey icons ────────────────
// Spec: "layered identity markers, intersecting circles, hands. Avoid
// identity-flag iconography — keep abstract + dignified." These icons
// represent EXPERIENCES of identity-related navigation, not the identities
// themselves. The visual vocabulary stays abstract (overlapping circles,
// soft silhouettes) so the icons don't appropriate or oversimplify.

export function IconJourneyNavigatingStigma({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Obstacle circles — what's being navigated around */}
    <circle cx="14" cy="14" r="3" stroke="var(--color-icon-accent)" strokeWidth="1.2" fill="var(--color-icon-belly)" fillOpacity="0.2" />
    <circle cx="28" cy="22" r="3" stroke="var(--color-icon-accent)" strokeWidth="1.2" fill="var(--color-icon-belly)" fillOpacity="0.2" />
    <circle cx="14" cy="30" r="3" stroke="var(--color-icon-accent)" strokeWidth="1.2" fill="var(--color-icon-belly)" fillOpacity="0.2" />
    {/* Winding path through them — agency in motion */}
    <path d="M6 20 Q12 20 18 14 Q24 14 22 22 Q20 30 28 28 Q34 27 34 20" stroke="var(--color-icon-warm-body)" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    {/* Citrine marker — the user, present + moving */}
    <circle cx="6" cy="20" r="1.8" fill="var(--color-icon-warm-body)" />
  </>);
}

export function IconJourneyBodyImage({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Soft abstract body silhouette — no gender / race specifics */}
    <path d="M20 8 Q23 8 23 11 Q23 13 21 14 Q26 16 26 22 Q26 30 25 32 L15 32 Q14 30 14 22 Q14 16 19 14 Q17 13 17 11 Q17 8 20 8 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M20 8 Q23 8 23 11 Q23 13 21 14 Q26 16 26 22 Q26 30 25 32 L15 32 Q14 30 14 22 Q14 16 19 14 Q17 13 17 11 Q17 8 20 8 Z" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    {/* Citrine glow at heart-centre — inner relation to self */}
    <circle cx="20" cy="22" r="2.5" fill="var(--color-icon-warm-body)" />
    <circle cx="20" cy="22" r="1.1" fill="var(--color-icon-warm-cheek)" />
  </>);
}

export function IconJourneyMigrationScd({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Plane silhouette — same shape as travel-flying, distinguished by an
     * internal Citrine marker instead of external trailing sparks. The
     * plane carries something WITH it (the condition); travel-flying just
     * goes and returns. */}
    <path d="M7 24 L20 13 L24 9 Q27 7 28 10 L24 16 L33 18 L33 21 L22 21 L18 26 L21 27 L21 29 L17 28 L14 28 L13 30 L10 29 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M7 24 L20 13 L24 9 Q27 7 28 10 L24 16 L33 18 L33 21 L22 21 L18 26 L21 27 L21 29 L17 28 L14 28 L13 30 L10 29 Z" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    {/* Citrine marker INSIDE the plane — carrying SCD across borders/systems */}
    <circle cx="20" cy="20" r="2.5" fill="var(--color-icon-warm-body)" />
    <circle cx="20" cy="20" r="1.1" fill="var(--color-icon-warm-cheek)" />
  </>);
}

export function IconJourneyFamilialStigma({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Family constellation — three overlapping circles, one set apart */}
    <circle cx="14" cy="14" r="5" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="var(--color-icon-belly)" fillOpacity="0.3" />
    <circle cx="22" cy="18" r="5" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="var(--color-icon-belly)" fillOpacity="0.3" />
    <circle cx="16" cy="24" r="5" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="var(--color-icon-belly)" fillOpacity="0.3" />
    {/* User — Citrine, slightly outside the cluster, still connected by a thin line */}
    <circle cx="30" cy="30" r="3" fill="var(--color-icon-warm-body)" />
    <circle cx="30" cy="30" r="1.4" fill="var(--color-icon-warm-cheek)" />
    <line x1="22" y1="25" x2="28" y2="29" stroke="var(--color-icon-accent)" strokeWidth="1" strokeLinecap="round" opacity="0.5" strokeDasharray="2 2" />
  </>);
}

export function IconJourneyCulturalIdentity({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Woven knot — two interlocking arcs forming a continuous loop */}
    <path d="M20 8 Q32 8 32 20 Q32 32 20 32" stroke="var(--color-icon-accent)" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <path d="M20 8 Q8 8 8 20 Q8 32 20 32" stroke="var(--color-icon-accent)" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    {/* Inner woven layer */}
    <path d="M20 14 Q26 14 26 20 Q26 26 20 26" stroke="var(--color-icon-warm-body)" strokeWidth="1.6" fill="none" strokeLinecap="round" />
    <path d="M20 14 Q14 14 14 20 Q14 26 20 26" stroke="var(--color-icon-warm-body)" strokeWidth="1.6" fill="none" strokeLinecap="round" />
    {/* Centre — Citrine focal */}
    <circle cx="20" cy="20" r="1.4" fill="var(--color-icon-warm-body)" />
  </>);
}

export function IconJourneyGenderIdentity({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Multiple layered concentric rings of varying opacity — multiplicity, no specific identity claim */}
    <circle cx="20" cy="20" r="13" stroke="var(--color-icon-accent)" strokeWidth="1.2" fill="none" opacity="0.3" />
    <circle cx="20" cy="20" r="10" stroke="var(--color-icon-accent)" strokeWidth="1.2" fill="none" opacity="0.5" />
    <circle cx="20" cy="20" r="7" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" opacity="0.75" />
    {/* Citrine core — the self at the centre of those layers */}
    <circle cx="20" cy="20" r="3.5" fill="var(--color-icon-warm-body)" />
    <circle cx="20" cy="20" r="1.6" fill="var(--color-icon-warm-cheek)" />
  </>);
}

export function IconJourneySexualOrientation({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Constellation of Citrine points + thin connecting threads —
     * multiplicity of attractions / relations / complexity. NOT
     * binary "two ends" iconography. */}
    <path d="M17 9 Q20 14 24 12" stroke="var(--color-icon-accent)" strokeWidth="0.5" fill="none" opacity="0.45" />
    <path d="M13 19 Q17 19 20 20" stroke="var(--color-icon-accent)" strokeWidth="0.5" fill="none" opacity="0.45" />
    <path d="M20 20 Q24 21 27 22" stroke="var(--color-icon-accent)" strokeWidth="0.5" fill="none" opacity="0.45" />
    <path d="M17 29 Q21 27 25 30" stroke="var(--color-icon-accent)" strokeWidth="0.5" fill="none" opacity="0.45" />
    <path d="M27 22 Q30 25 32 27" stroke="var(--color-icon-accent)" strokeWidth="0.5" fill="none" opacity="0.45" />
    <circle cx="10" cy="13" r="1.3" fill="var(--color-icon-warm-body)" opacity="0.6" />
    <circle cx="17" cy="9" r="1.5" fill="var(--color-icon-warm-body)" opacity="0.75" />
    <circle cx="24" cy="12" r="1.2" fill="var(--color-icon-warm-body)" opacity="0.55" />
    <circle cx="31" cy="15" r="1.6" fill="var(--color-icon-warm-body)" opacity="0.8" />
    <circle cx="13" cy="19" r="1.4" fill="var(--color-icon-warm-body)" />
    <circle cx="20" cy="20" r="2.2" fill="var(--color-icon-warm-body)" />
    <circle cx="20" cy="20" r="1" fill="var(--color-icon-warm-cheek)" />
    <circle cx="27" cy="22" r="1.5" fill="var(--color-icon-warm-body)" opacity="0.85" />
    <circle cx="9" cy="26" r="1.2" fill="var(--color-icon-warm-body)" opacity="0.6" />
    <circle cx="17" cy="29" r="1.6" fill="var(--color-icon-warm-body)" opacity="0.75" />
    <circle cx="25" cy="30" r="1.3" fill="var(--color-icon-warm-body)" opacity="0.65" />
    <circle cx="32" cy="27" r="1.4" fill="var(--color-icon-warm-body)" opacity="0.7" />
  </>);
}

// ─── Wave 2.5 Chunk E — Health body journey icons ─────────────────────────
// Spec: "lunar cycles, cycles + growth, gentle medical metaphors."

export function IconJourneySexualHealth({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Rose Quartz heart base — extends the journey-sexual visual language */}
    <path d="M20 14 Q15 10 11 12 Q7 14 8 19 Q9 25 20 32 Q31 25 32 19 Q33 14 29 12 Q25 10 20 14 Z" fill="var(--color-icon-rose-body)" opacity="0.4" />
    <path d="M20 14 Q15 10 11 12 Q7 14 8 19 Q9 25 20 32 Q31 25 32 19 Q33 14 29 12 Q25 10 20 14 Z" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    {/* Citrine cross — the health/care dimension */}
    <line x1="20" y1="17" x2="20" y2="25" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" />
    <line x1="16" y1="21" x2="24" y2="21" stroke="var(--color-icon-warm-body)" strokeWidth="2" strokeLinecap="round" />
  </>);
}

export function IconJourneyMenstrualHealth({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Lunar cycle — three moon phases */}
    {/* Waxing crescent */}
    <circle cx="10" cy="20" r="4.5" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" />
    <path d="M10 15.5 Q13 17 13 20 Q13 23 10 24.5 Q12 23 12 20 Q12 17 10 15.5 Z" fill="var(--color-icon-belly)" opacity="0.5" />
    {/* Full moon — centre, Citrine */}
    <circle cx="20" cy="20" r="5" fill="var(--color-icon-warm-body)" />
    <circle cx="20" cy="20" r="2.5" fill="var(--color-icon-warm-cheek)" opacity="0.8" />
    {/* Waning crescent */}
    <circle cx="30" cy="20" r="4.5" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" />
    <path d="M30 15.5 Q27 17 27 20 Q27 23 30 24.5 Q28 23 28 20 Q28 17 30 15.5 Z" fill="var(--color-icon-belly)" opacity="0.5" />
  </>);
}

export function IconJourneyFamilyPlanning({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Single trunk path — the starting place */}
    <line x1="20" y1="33" x2="20" y2="22" stroke="var(--color-icon-accent)" strokeWidth="2" strokeLinecap="round" />
    {/* Three diverging path branches — possibilities */}
    <path d="M20 22 Q15 18 9 12" stroke="var(--color-icon-accent)" strokeWidth="2" fill="none" strokeLinecap="round" />
    <path d="M20 22 Q20 16 20 8" stroke="var(--color-icon-accent)" strokeWidth="2" fill="none" strokeLinecap="round" />
    <path d="M20 22 Q25 18 31 12" stroke="var(--color-icon-accent)" strokeWidth="2" fill="none" strokeLinecap="round" />
    {/* Citrine markers at each tip — choices */}
    <circle cx="9" cy="12" r="1.6" fill="var(--color-icon-warm-body)" />
    <circle cx="20" cy="8" r="1.6" fill="var(--color-icon-warm-body)" />
    <circle cx="31" cy="12" r="1.6" fill="var(--color-icon-warm-body)" />
  </>);
}

export function IconJourneyFertility({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Closed bud — potential, not pathologised */}
    <path d="M20 11 Q14 14 14 22 Q14 28 20 30 Q26 28 26 22 Q26 14 20 11 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M20 11 Q14 14 14 22 Q14 28 20 30 Q26 28 26 22 Q26 14 20 11 Z" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Stem */}
    <line x1="20" y1="30" x2="20" y2="35" stroke="var(--color-icon-accent)" strokeWidth="1.8" strokeLinecap="round" />
    {/* Citrine inner glow — quiet readiness */}
    <circle cx="20" cy="22" r="2.5" fill="var(--color-icon-warm-body)" />
    <circle cx="20" cy="22" r="1.2" fill="var(--color-icon-warm-cheek)" />
    {/* Subtle stamen-like marks at the tip */}
    <line x1="20" y1="11" x2="18" y2="8" stroke="var(--color-icon-accent)" strokeWidth="0.8" strokeLinecap="round" opacity="0.7" />
    <line x1="20" y1="11" x2="22" y2="8" stroke="var(--color-icon-accent)" strokeWidth="0.8" strokeLinecap="round" opacity="0.7" />
    <line x1="20" y1="11" x2="20" y2="7" stroke="var(--color-icon-accent)" strokeWidth="0.8" strokeLinecap="round" opacity="0.7" />
  </>);
}

export function IconJourneyPregnancyScd({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Soft round belly silhouette — abstract, dignified */}
    <path d="M16 11 Q14 12 14 17 Q12 18 12 24 Q12 32 18 33 Q22 33 26 32 Q30 30 30 24 Q30 18 28 17 Q28 13 26 12" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M16 11 Q14 12 14 17 Q12 18 12 24 Q12 32 18 33 Q22 33 26 32 Q30 30 30 24 Q30 18 28 17 Q28 13 26 12" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Citrine inner glow — the new life */}
    <circle cx="22" cy="24" r="3" fill="var(--color-icon-warm-body)" />
    <circle cx="22" cy="24" r="1.4" fill="var(--color-icon-warm-cheek)" />
  </>);
}

export function IconJourneyPainManagement({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Wave being calmed — bigger amplitude on left, settling on right */}
    <path d="M5 20 Q8 12 11 20 Q14 28 17 20 Q19.5 16 22 20 Q24 22 26 20 Q29 19 32 20 Q34 20 35 20" stroke="var(--color-icon-accent)" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    {/* Citrine line below — the held / managed baseline */}
    <line x1="5" y1="28" x2="35" y2="28" stroke="var(--color-icon-warm-body)" strokeWidth="1.8" strokeLinecap="round" />
    {/* Citrine marker — point of intervention */}
    <circle cx="22" cy="20" r="1.8" fill="var(--color-icon-warm-body)" />
  </>);
}

// ─── Wave 2.5 Chunk F — Relationships + Work + Finance journey icons ──────
// Spec: "two-figure compositions for relationships, plane silhouettes for
// travel, shield + scale for rights/insurance."

export function IconJourneyRelationships({ size = 24, ariaLabel }: CrystalIconProps) {
  // Silhouette adapted from svgrepo bird-love-heart-svgrepo-com.svg
  // (512×512 viewBox). Scaled to 40×40 via scale(0.078125). Top heart in
  // Citrine warm; two birds + heart-decorations in amethyst body.
  return svg(size, ariaLabel, <g transform="scale(0.078125)">
    {/* Top heart between the birds — Citrine warm (the relationship's centre) */}
    <path d="M210.239,161.63c-11.85-11.979-13.889-31.229-3.614-44.605c12.414-16.161,35.529-17.243,49.375-3.245c13.847-13.998,36.961-12.917,49.375,3.245c10.275,13.376,8.235,32.626-3.614,44.605l-32.5,32.855c-7.318,7.398-19.204,7.398-26.522,0L210.239,161.63z" fill="var(--color-icon-warm-body)" />
    {/* Left bird wing-heart accent */}
    <path d="M130.272,174.126c3.14,3.22,8.315,3.22,11.455,0l19.952-20.46c7.017-7.196,8.387-18.749,2.104-26.593c-7.145-8.922-20.021-9.403-27.784-1.442c-7.763-7.96-20.64-7.479-27.784,1.442c-6.283,7.845-4.913,19.397,2.104,26.593L130.272,174.126z" fill="var(--color-icon-body)" />
    {/* Right bird wing-heart accent */}
    <path d="M370.272,174.126c3.14,3.22,8.315,3.22,11.455,0l19.952-20.46c7.017-7.196,8.387-18.749,2.104-26.593c-7.145-8.922-20.021-9.403-27.784-1.442c-7.763-7.96-20.64-7.479-27.784,1.442c-6.283,7.845-4.913,19.397,2.104,26.593L370.272,174.126z" fill="var(--color-icon-body)" />
    {/* Two birds (left + right, facing each other) — amethyst body */}
    <path d="M468.72,408h-31.44c-8.48,0-16.64-3.36-22.64-9.36L408,392c-36.56,25.04-85.76,20.48-117.12-10.88C259.52,349.76,254.96,300.56,280,264c-2.96-7.92-4-16.24-3.12-24.32c0-0.006,0.002-0.012,0.003-0.018L256,223.966l28.838-7.175l-0.109,0.315c6.446-10.327,16.483-18.683,29.512-22.866c12.96-4.16,27.44-2.96,39.52,3.28c25.653,13.224,35.246,42.292,25.851,66.778l84.366,84.366c4.609,4.605,5.969,11.469,3.477,17.484c-2.099,5.073-6.62,8.462-11.878,9.426l18.745,18.745C479.36,399.36,475.84,408,468.72,408z M320,232c0-4.4-3.6-8-8-8s-8,3.6-8,8s3.6,8,8,8S320,236.4,320,232z M452.789,359.996L376,283.309l-11.547,11.547c-6.68,6.688-10.344,15.59-10.313,25.063c0.031,9.484,3.766,18.371,10.516,25.027c4.609,4.551,10.711,7.055,17.188,7.055h14.844l-26.344-26.344c-3.125-3.125-3.125-8.188,0-11.313s8.188-3.125,11.313,0l39.779,39.702c4.418,3.835,10.037,5.955,15.846,5.955L452.789,359.996z M227.162,216.791L256,223.966l-20.883,15.696c0,0.006,0.002,0.012,0.003,0.018c0.88,8.08-0.16,16.4-3.12,24.32c25.04,36.56,20.48,85.76-10.88,117.12C189.76,412.48,140.56,417.04,104,392l-6.64,6.64c-6,6-14.16,9.36-22.64,9.36H43.28c-7.12,0-10.64-8.64-5.6-13.68l18.745-18.745c-5.257-0.965-9.78-4.354-11.882-9.426c-2.492-6.016-1.125-12.879,3.48-17.484l84.366-84.366c-9.395-24.487,0.198-53.555,25.851-66.778c12.08-6.24,26.56-7.44,39.52-3.28c13.028,4.183,23.066,12.539,29.512,22.866L227.162,216.791z M157.859,319.914c0.031-9.473-3.633-18.371-10.316-25.059L136,283.313l-76.664,76.664L74.719,360c5.838,0,11.493-2.135,15.916-6.006l0.615-0.584l39.094-39.066c3.125-3.125,8.188-3.125,11.313,0s3.125,8.188,0,11.313L115.279,352h14.881c6.469,0,12.57-2.504,17.184-7.055C154.094,338.289,157.828,329.398,157.859,319.914z M208,232c0-4.4-3.6-8-8-8s-8,3.6-8,8s3.6,8,8,8S208,236.4,208,232z" fill="var(--color-icon-body)" />
  </g>);
}

export function IconJourneyDisclosure({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Speech bubble / opening shape */}
    <path d="M8 12 L26 12 Q30 12 30 16 L30 24 Q30 28 26 28 L15 28 L9 33 L11 28 Q8 28 8 24 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M8 12 L26 12 Q30 12 30 16 L30 24 Q30 28 26 28 L15 28 L9 33 L11 28 Q8 28 8 24 Z" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Citrine spark — the truth being spoken */}
    <circle cx="19" cy="20" r="2.5" fill="var(--color-icon-warm-body)" />
    <circle cx="19" cy="20" r="1.1" fill="var(--color-icon-warm-cheek)" />
  </>);
}

export function IconJourneyTravelFlying({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Plane silhouette — angled across the icon */}
    <path d="M7 24 L20 13 L24 9 Q27 7 28 10 L24 16 L33 18 L33 21 L22 21 L18 26 L21 27 L21 29 L17 28 L14 28 L13 30 L10 29 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M7 24 L20 13 L24 9 Q27 7 28 10 L24 16 L33 18 L33 21 L22 21 L18 26 L21 27 L21 29 L17 28 L14 28 L13 30 L10 29 Z" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    {/* Citrine trailing spark */}
    <circle cx="6" cy="27" r="1.2" fill="var(--color-icon-warm-body)" opacity="0.85" />
    <circle cx="4" cy="30" r="0.8" fill="var(--color-icon-warm-body)" opacity="0.6" />
  </>);
}

export function IconJourneyCareerWorkplace({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Stepped path — rising career */}
    <path d="M6 32 L14 32 L14 24 L22 24 L22 16 L30 16 L30 8" stroke="var(--color-icon-accent)" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    {/* Inner softer line on the stepped path */}
    <path d="M6 32 L14 32 L14 24 L22 24 L22 16 L30 16 L30 8" stroke="var(--color-icon-belly)" strokeWidth="1" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
    {/* Citrine marker at the top — destination */}
    <circle cx="30" cy="8" r="2.4" fill="var(--color-icon-warm-body)" />
    <circle cx="30" cy="8" r="1.1" fill="var(--color-icon-warm-cheek)" />
    {/* Start marker at the bottom */}
    <circle cx="6" cy="32" r="1.6" fill="var(--color-icon-accent)" />
  </>);
}

export function IconJourneyFinancialInsurance({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Shield silhouette — protection */}
    <path d="M20 7 L31 11 L31 21 Q31 28 20 33 Q9 28 9 21 L9 11 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M20 7 L31 11 L31 21 Q31 28 20 33 Q9 28 9 21 L9 11 Z" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Citrine dollar accent — financial dimension */}
    <line x1="20" y1="14" x2="20" y2="26" stroke="var(--color-icon-warm-body)" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M23 17 Q23 15 20 15 Q17 15 17 17.5 Q17 19 20 20 Q23 21 23 22.5 Q23 25 20 25 Q17 25 17 23" stroke="var(--color-icon-warm-body)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </>);
}

export function IconJourneyAccommodationRequests({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Document body */}
    <path d="M9 8 L25 8 L31 14 L31 32 Q31 33 30 33 L10 33 Q9 33 9 32 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M9 8 L25 8 L31 14 L31 32 Q31 33 30 33 L10 33 Q9 33 9 32 Z" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Folded corner */}
    <path d="M25 8 L31 14 L25 14 Z" fill="var(--color-icon-accent)" opacity="0.4" />
    {/* Lines of text */}
    <line x1="13" y1="19" x2="27" y2="19" stroke="var(--color-icon-accent)" strokeWidth="1.2" strokeLinecap="round" opacity="0.6" />
    <line x1="13" y1="23" x2="27" y2="23" stroke="var(--color-icon-accent)" strokeWidth="1.2" strokeLinecap="round" opacity="0.6" />
    {/* Citrine check — request fulfilled / formal ask */}
    <path d="M14 28 L17 30 L23 26" stroke="var(--color-icon-warm-body)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </>);
}

export function IconJourneyEmploymentRights({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Balance / scales pivot */}
    <line x1="20" y1="10" x2="20" y2="32" stroke="var(--color-icon-accent)" strokeWidth="1.8" strokeLinecap="round" />
    {/* Crossbar */}
    <line x1="8" y1="14" x2="32" y2="14" stroke="var(--color-icon-accent)" strokeWidth="1.8" strokeLinecap="round" />
    {/* Left pan suspension */}
    <line x1="10" y1="14" x2="10" y2="18" stroke="var(--color-icon-accent)" strokeWidth="1" />
    {/* Right pan suspension */}
    <line x1="30" y1="14" x2="30" y2="18" stroke="var(--color-icon-accent)" strokeWidth="1" />
    {/* Left pan */}
    <path d="M6 18 Q10 22 14 18" stroke="var(--color-icon-accent)" strokeWidth="1.5" fill="var(--color-icon-belly)" fillOpacity="0.3" strokeLinejoin="round" />
    {/* Right pan */}
    <path d="M26 18 Q30 22 34 18" stroke="var(--color-icon-accent)" strokeWidth="1.5" fill="var(--color-icon-belly)" fillOpacity="0.3" strokeLinejoin="round" />
    {/* Citrine base — what holds the scale */}
    <rect x="15" y="32" width="10" height="2" rx="1" fill="var(--color-icon-warm-body)" />
  </>);
}

// ─── Utility icons (added 2026-05-21 for the App.tsx emoji wire-in pass) ─
// These cover surfaces where the existing Crystal-vocab journey/utility
// icons don't fit: grounding/meditation, solidarity/resistance, identity
// label, advocacy/focus, care-team, hearts (with optional tint), and the
// universal UI affordances (check / close / warning / sparkle).

export function IconGrounding({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Mountain range — stable, anchored, immovable */}
    {/* Back-left peak (shorter, lighter) */}
    <path d="M4 32 L13 14 L22 32 Z" fill="var(--color-icon-belly)" opacity="0.4" />
    <path d="M4 32 L13 14 L22 32 Z" stroke="var(--color-icon-accent)" strokeWidth="1.5" fill="none" strokeLinejoin="round" />
    {/* Snow cap on back-left peak */}
    <path d="M10 19 L13 14 L16 19 Q15 21 13 21 Q11 21 10 19 Z" fill="var(--color-icon-cheek)" opacity="0.5" />
    {/* Front-right peak (taller, darker, dominant) */}
    <path d="M14 32 L25 9 L36 32 Z" fill="var(--color-icon-body)" opacity="0.55" />
    <path d="M14 32 L25 9 L36 32 Z" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Snow cap on front-right peak */}
    <path d="M21 16 L25 9 L29 16 Q27 18 25 18 Q23 18 21 16 Z" fill="var(--color-icon-cheek)" opacity="0.7" />
    {/* Citrine sun behind the peak */}
    <circle cx="29" cy="12" r="3.2" fill="var(--color-icon-warm-body)" />
    <circle cx="29" cy="12" r="1.5" fill="var(--color-icon-warm-cheek)" />
    {/* Ground line at the base */}
    <line x1="4" y1="33" x2="36" y2="33" stroke="var(--color-icon-accent)" strokeWidth="1.4" strokeLinecap="round" />
  </>);
}

export function IconSolidarity({ size = 24, ariaLabel }: CrystalIconProps) {
  // Silhouette adapted from svgrepo love-gesture-hand-svgrepo-com.svg
  // (512×512 viewBox). Scaled to 40×40 via scale(0.078125). Two paths:
  // top heart in Citrine warm-body; hand (ILY sign) in amethyst body.
  return svg(size, ariaLabel, <g transform="scale(0.078125)">
    {/* Heart at top — Citrine warm (the love being held up) */}
    <path d="M230.32,105.666c-7.017-7.196-8.387-18.749-2.104-26.593c7.145-8.922,20.021-9.403,27.784-1.442c7.763-7.96,20.64-7.479,27.784,1.442c6.283,7.845,4.913,19.397-2.104,26.593l-19.952,20.46c-3.14,3.22-8.315,3.22-11.455,0L230.32,105.666z" fill="var(--color-icon-warm-body)" />
    {/* Hand — amethyst body (the gesture, the holding-up) */}
    <path d="M436.88,313.44l-24.16-50.8l-40.24-84.48c-2.48-5.2-6.32-9.68-11.12-12.88l-21.04-14c-7.52-5.04-17.28-5.36-25.2-0.88l-44.64,25.44c-4.08,2.08-6.88,5.6-8.16,9.68l2.16,2.16c5.04,5.04,12.88,6.16,19.12,2.64l21.68-12.08c5.92-3.28,12.64-5.04,19.44-5.04c9.84,0,19.28,3.6,26.64,10.16l53.092,68.597L340.72,195.28c-7.6-6.72-18.72-8-27.6-3.04l-40.64,22.64C262.32,220.48,256,231.2,256,242.8c0-11.6-6.32-22.32-16.48-27.92l-40.64-22.64c-8.88-4.96-20-3.68-27.6,3.04l-63.731,56.677l53.092-68.597c7.36-6.56,16.8-10.16,26.64-10.16c6.8,0,13.52,1.76,19.44,5.04l21.68,12.08c6.645,3.748,13.659,2.821,21.28-4.8c-1.28-4.08-4.08-7.6-8.16-9.68l-44.64-25.44c-7.92-4.48-17.68-4.16-25.2,0.88l-21.04,14c-4.8,3.2-8.64,7.68-11.12,12.88l-40.24,84.48l-24.16,50.8c-2.08,4.32-3.12,9.04-3.12,13.76V408h88v-8c-8,0-15.44-1.76-22-5.12l-10.88-5.44c-7.84-3.92-11.04-13.52-7.12-21.44l25.28,12.64c4.4,2.24,9.28,3.36,14.24,3.36h46.16c1.52,0,2.96,0.48,4.24,1.28l11.2,7.44c6.32,4.24,14.8,3.36,20.16-2l3.44-3.44c6.24-6.24,6.24-16.32,0-22.56l-16.4-16.4c-2.88-2.88-6.24-5.12-10-6.72l-45.44-19.52c-2.96-1.28-4.88-4.16-4.88-7.44v-47.36c0-2.08,0.8-4.08,2.32-5.6l18.16-18.16c2.08-2.08,5.28-2.88,8.16-1.92l13.92,4.56c3.2,1.12,5.44,4.16,5.44,7.6V256c0,8.8,7.2,16,16,16h8c8.8,0,16-7.2,16-16c0,8.8,7.2,16,16,16h8c8.8,0,16-7.2,16-16v-2.24c0-3.44,2.24-6.48,5.44-7.6l13.92-4.56c2.88-0.96,6.08-0.16,8.16,1.92l18.16,18.16c1.52,1.52,2.32,3.52,2.32,5.6v47.36c0,3.28-1.92,6.16-4.88,7.44l-45.44,19.52c-3.76,1.6-7.12,3.84-10,6.72l-16.4,16.4c-6.24,6.24-6.24,16.32,0,22.56l3.44,3.44c5.36,5.36,13.84,6.24,20.16,2l11.2-7.44c1.28-0.8,2.72-1.28,4.24-1.28h46.16c4.96,0,9.84-1.12,14.24-3.36L392,368c3.92,7.92,0.72,17.52-7.12,21.44L374,394.88c-6.56,3.36-14,5.12-22,5.12v8h88v-80.8C440,322.48,438.96,317.76,436.88,313.44z M368,440h56c8.836,0,16-7.164,16-16h-88C352,432.836,359.164,440,368,440z M88,440h56c8.836,0,16-7.164,16-16H72C72,432.836,79.164,440,88,440z" fill="var(--color-icon-body)" />
  </g>);
}

export function IconIdentityTag({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Tag silhouette — rectangle with triangular notch + hole */}
    <path d="M6 12 Q6 10 8 10 L28 10 L34 20 L28 30 L8 30 Q6 30 6 28 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <path d="M6 12 Q6 10 8 10 L28 10 L34 20 L28 30 L8 30 Q6 30 6 28 Z" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    {/* Tag string-hole */}
    <circle cx="12" cy="20" r="1.8" fill="var(--color-icon-warm-body)" />
    <circle cx="12" cy="20" r="0.8" fill="var(--color-bg-card)" />
    {/* A single horizontal line — the label content */}
    <line x1="18" y1="20" x2="28" y2="20" stroke="var(--color-icon-accent)" strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
  </>);
}

export function IconAdvocacy({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Target rings — focused intent */}
    <circle cx="22" cy="20" r="11" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" opacity="0.35" />
    <circle cx="22" cy="20" r="7" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" opacity="0.6" />
    <circle cx="22" cy="20" r="3.5" fill="var(--color-icon-warm-body)" />
    <circle cx="22" cy="20" r="1.5" fill="var(--color-icon-warm-cheek)" />
    {/* Arrow striking toward the bullseye — agency / advocacy */}
    <line x1="6" y1="12" x2="18" y2="18" stroke="var(--color-icon-accent)" strokeWidth="2" strokeLinecap="round" />
    <path d="M14 16 L18 18 L15 21" stroke="var(--color-icon-accent)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </>);
}

export function IconCareTeam({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Three soft circles — a team of three, slightly offset */}
    <circle cx="14" cy="14" r="5" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <circle cx="14" cy="14" r="5" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" />
    <circle cx="26" cy="14" r="5" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <circle cx="26" cy="14" r="5" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" />
    <circle cx="20" cy="26" r="5" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    <circle cx="20" cy="26" r="5" stroke="var(--color-icon-accent)" strokeWidth="1.4" fill="none" />
    {/* Citrine heart at the centre where care meets */}
    <path d="M20 18 Q18 16 16 17 Q15 18 16 20 Q17 22 20 23 Q23 22 24 20 Q25 18 24 17 Q22 16 20 18 Z" fill="var(--color-icon-warm-body)" />
  </>);
}

export function IconHeart({ size = 24, ariaLabel, heartTint }: CrystalIconProps) {
  // Optional heartTint lets callers render mood-vocab heart colours
  // (💜 amethyst → use var(--color-icon-accent); 💛 yellow → warm-body;
  // ❤️ red → e.g. #e25c5c; 💔 muted → text-secondary; etc.).
  const fill = heartTint ?? 'var(--color-icon-warm-body)';
  return svg(size, ariaLabel, <>
    <path d="M20 13 Q15 8 11 10 Q7 12 8 18 Q9 24 20 32 Q31 24 32 18 Q33 12 29 10 Q25 8 20 13 Z" fill={fill} opacity="0.85" />
    <path d="M20 13 Q15 8 11 10 Q7 12 8 18 Q9 24 20 32 Q31 24 32 18 Q33 12 29 10 Q25 8 20 13 Z" stroke="var(--color-icon-accent)" strokeWidth="1.2" fill="none" opacity="0.7" />
  </>);
}

export function IconCheck({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Bold checkmark — universal "done / saved" affordance */}
    <path d="M8 21 L17 30 L32 11" stroke="var(--color-icon-accent)" strokeWidth="3.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </>);
}

export function IconClose({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* X — universal close / remove affordance */}
    <line x1="10" y1="10" x2="30" y2="30" stroke="var(--color-icon-accent)" strokeWidth="2.8" strokeLinecap="round" />
    <line x1="30" y1="10" x2="10" y2="30" stroke="var(--color-icon-accent)" strokeWidth="2.8" strokeLinecap="round" />
  </>);
}

export function IconWarning({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Triangle with Citrine warning fill */}
    <path d="M20 6 L34 30 Q35 32 33 32 L7 32 Q5 32 6 30 Z" fill="var(--color-icon-warm-body)" opacity="0.3" />
    <path d="M20 6 L34 30 Q35 32 33 32 L7 32 Q5 32 6 30 Z" stroke="var(--color-icon-accent)" strokeWidth="1.8" fill="none" strokeLinejoin="round" />
    {/* Exclamation bar */}
    <rect x="18.5" y="14" width="3" height="10" rx="1.4" fill="var(--color-icon-accent)" />
    {/* Exclamation dot */}
    <circle cx="20" cy="28" r="1.6" fill="var(--color-icon-accent)" />
  </>);
}

export function IconSparkle({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Four-pointed Citrine sparkle / star burst */}
    <path d="M20 5 L22 17 L20 19 L18 17 Z" fill="var(--color-icon-warm-body)" />
    <path d="M20 35 L22 23 L20 21 L18 23 Z" fill="var(--color-icon-warm-body)" />
    <path d="M5 20 L17 22 L19 20 L17 18 Z" fill="var(--color-icon-warm-body)" />
    <path d="M35 20 L23 22 L21 20 L23 18 Z" fill="var(--color-icon-warm-body)" />
    {/* Small Citrine-cheek glow at the centre */}
    <circle cx="20" cy="20" r="1.6" fill="var(--color-icon-warm-cheek)" />
  </>);
}

// ─── Top-30 batch 1 (2026-05-21 evening) ─────────────────────────────
// Six low-risk utility icons replacing the most-repeated emojis in the
// data-schema + UI-affordance sweep: 📋 💡 🛡 💬 📞 🔒.

export function IconChecklist({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Clipboard body */}
    <rect x="6" y="8" width="28" height="29" rx="4" fill="var(--color-icon-body)" />
    {/* Clip at top */}
    <rect x="14" y="4" width="12" height="6" rx="2" fill="var(--color-icon-accent)" />
    {/* Belly inset */}
    <rect x="9" y="13" width="22" height="20" rx="2" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Row 1: checked */}
    <path d="M11.5 19 L13.5 21 L17 17.5" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="19" y1="19" x2="29" y2="19" stroke="var(--color-icon-cheek)" strokeWidth="1.5" strokeLinecap="round" opacity="0.85" />
    {/* Row 2: checked */}
    <path d="M11.5 25 L13.5 27 L17 23.5" stroke="var(--color-icon-accent)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
    <line x1="19" y1="25" x2="27" y2="25" stroke="var(--color-icon-cheek)" strokeWidth="1.5" strokeLinecap="round" opacity="0.75" />
    {/* Row 3: unchecked */}
    <circle cx="14" cy="31" r="2" fill="none" stroke="var(--color-icon-accent)" strokeWidth="1.4" opacity="0.7" />
    <line x1="19" y1="31" x2="25" y2="31" stroke="var(--color-icon-cheek)" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
  </>);
}

export function IconLightbulb({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Bulb body (warm Citrine — this icon carries the warmth metaphor) */}
    <path d="M20 5 Q28 5 30 13 Q31 17 28 22 Q26 25 25 28 L15 28 Q14 25 12 22 Q9 17 10 13 Q12 5 20 5 Z" fill="var(--color-icon-warm-body)" />
    {/* Inner glow */}
    <path d="M20 8 Q26 8 27.5 14 Q28.5 17 26.5 21 Q25 24 24 26 L16 26 Q15 24 13.5 21 Q11.5 17 12.5 14 Q14 8 20 8 Z" fill="var(--color-icon-warm-cheek)" opacity="0.7" />
    {/* Filament hint */}
    <path d="M17 16 Q20 14 23 16 M17 19 Q20 17 23 19" stroke="var(--color-icon-accent)" strokeWidth="1.1" fill="none" strokeLinecap="round" opacity="0.55" />
    {/* Screw base bands */}
    <rect x="15" y="29" width="10" height="2" rx="0.6" fill="var(--color-icon-accent)" />
    <rect x="15" y="32" width="10" height="2" rx="0.6" fill="var(--color-icon-accent)" opacity="0.75" />
    {/* Contact tip */}
    <path d="M17 35 L23 35 L22 37 L18 37 Z" fill="var(--color-icon-accent)" />
  </>);
}

export function IconShield({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Shield outer */}
    <path d="M20 4 L34 9 L34 20 Q34 28 20 36 Q6 28 6 20 L6 9 Z" fill="var(--color-icon-body)" />
    {/* Inner belly inset */}
    <path d="M20 7 L31 11 L31 20 Q31 26 20 32 Q9 26 9 20 L9 11 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Centre crest mark */}
    <path d="M20 13 L26 15.5 L26 20 Q26 24 20 27 Q14 24 14 20 L14 15.5 Z" fill="var(--color-icon-accent)" opacity="0.55" />
    {/* Small Citrine accent at top — protective warmth */}
    <circle cx="20" cy="10" r="1.4" fill="var(--color-icon-warm-body)" />
  </>);
}

export function IconSpeechBubble({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Bubble body with tail bottom-left */}
    <path d="M8 6 Q5 6 5 9 L5 24 Q5 27 8 27 L12 27 L10 34 L18 27 L32 27 Q35 27 35 24 L35 9 Q35 6 32 6 Z" fill="var(--color-icon-body)" />
    {/* Inner belly */}
    <path d="M9 9 Q7 9 7 11 L7 22 Q7 24 9 24 L31 24 Q33 24 33 22 L33 11 Q33 9 31 9 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Two text lines */}
    <line x1="11" y1="14" x2="29" y2="14" stroke="var(--color-icon-cheek)" strokeWidth="1.6" strokeLinecap="round" opacity="0.85" />
    <line x1="11" y1="19" x2="25" y2="19" stroke="var(--color-icon-cheek)" strokeWidth="1.6" strokeLinecap="round" opacity="0.7" />
  </>);
}

export function IconPhone({ size = 24, ariaLabel }: CrystalIconProps) {
  // Silhouette adapted from svgrepo phone-svgrepo-com.svg (24×24 viewBox).
  // Scaled to 40×40 viewBox via uniform scale(1.667). Pure amethyst — Citrine
  // dropped because a phone has no warm semantic referent (it's an object,
  // not a flame/bulb/beacon).
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <path d="M2.00589 4.54166C1.905 3.11236 3.11531 2 4.54522 2H7.60606C8.34006 2 9.00207 2.44226 9.28438 3.1212L10.5643 6.19946C10.8761 6.94932 10.6548 7.81544 10.0218 8.32292L9.22394 8.96254C8.86788 9.24798 8.74683 9.74018 8.95794 10.1448C10.0429 12.2241 11.6464 13.9888 13.5964 15.2667C14.008 15.5364 14.5517 15.4291 14.8588 15.0445L15.6902 14.003C16.1966 13.3687 17.0609 13.147 17.8092 13.4594L20.8811 14.742C21.5587 15.0249 22 15.6883 22 16.4238V19.5C22 20.9329 20.8489 22.0955 19.4226 21.9941C10.3021 21.3452 2.65247 13.7017 2.00589 4.54166Z" fill="var(--color-icon-body)" />
  </g>);
}

export function IconLock({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Shackle (the arc) */}
    <path d="M12 17 L12 11 Q12 4 20 4 Q28 4 28 11 L28 17 L25 17 L25 11 Q25 7 20 7 Q15 7 15 11 L15 17 Z" fill="var(--color-icon-accent)" />
    {/* Body */}
    <rect x="7" y="17" width="26" height="20" rx="3.5" fill="var(--color-icon-body)" />
    {/* Belly inset */}
    <rect x="10" y="20" width="20" height="14" rx="2" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Keyhole */}
    <circle cx="20" cy="25" r="2.4" fill="var(--color-icon-accent)" />
    <rect x="18.9" y="26" width="2.2" height="6" rx="0.6" fill="var(--color-icon-accent)" />
  </>);
}

// ─── Top-30 batch 2 (2026-05-21 evening) ─────────────────────────────
// Five utility icons replacing 💭 🔍 📚 📊 🚨.

export function IconThoughtBubble({ size = 24, ariaLabel }: CrystalIconProps) {
  // Silhouette adapted from svgrepo thought-svgrepo-com.svg (17×17 viewBox).
  // Scaled to 40×40 via scale(2.353). Solid cloud + two trailing solid
  // bubbles going down-right — matches the rest of CrystalIcons (all
  // solid silhouettes). Outlined-donut variant rejected because it
  // would have been a one-off pattern in the registry.
  return svg(size, ariaLabel, <g transform="scale(2.353)">
    {/* Cloud (solid) */}
    <path d="M11.5 10.116c3.033 0 5.5-2.269 5.5-5.058s-2.467-5.058-5.5-5.058c-1.912 0-3.693 0.938-4.684 2.431-0.705-0.399-1.496-0.608-2.316-0.608-2.481 0-4.5 1.86-4.5 4.147 0 2.286 2.019 4.146 4.5 4.146 0.186 0 0.375-0.013 0.573-0.037 0.652 0.588 1.522 0.921 2.427 0.921 1.002 0 1.929-0.387 2.592-1.070 0.488 0.125 0.951 0.186 1.408 0.186z" fill="var(--color-icon-body)" />
    {/* Big trailing bubble (solid) */}
    <path d="M11.5 10.5c-1.103 0-2 0.897-2 2s0.897 2 2 2 2-0.897 2-2-0.897-2-2-2z" fill="var(--color-icon-body)" />
    {/* Small trailing bubble (solid) */}
    <path d="M15.25 14c-0.689 0-1.25 0.561-1.25 1.25s0.561 1.25 1.25 1.25 1.25-0.561 1.25-1.25-0.561-1.25-1.25-1.25z" fill="var(--color-icon-body)" />
  </g>);
}

export function IconMagnifier({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Lens body */}
    <circle cx="16" cy="16" r="9.5" fill="var(--color-icon-body)" />
    {/* Lens glass (belly) */}
    <circle cx="16" cy="16" r="6.5" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Highlight glint */}
    <path d="M 11 12 Q 11 14 13 14" stroke="var(--color-icon-cheek)" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity="0.7" />
    {/* Handle */}
    <rect x="22" y="22" width="14" height="4.5" rx="2.2" transform="rotate(45 24 24)" fill="var(--color-icon-accent)" />
  </>);
}

export function IconBook({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Open book pages */}
    <path d="M 5 10 L 20 8 L 35 10 L 35 32 L 20 30 L 5 32 Z" fill="var(--color-icon-body)" />
    {/* Left page belly inset */}
    <path d="M 7 12 L 19 10.5 L 19 28.5 L 7 30 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Right page belly inset */}
    <path d="M 21 10.5 L 33 12 L 33 30 L 21 28.5 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Spine */}
    <line x1="20" y1="8" x2="20" y2="30" stroke="var(--color-icon-accent)" strokeWidth="1.2" />
    {/* Page lines (left) */}
    <line x1="9" y1="15" x2="17" y2="13.8" stroke="var(--color-icon-cheek)" strokeWidth="1.3" strokeLinecap="round" opacity="0.85" />
    <line x1="9" y1="19" x2="17" y2="17.8" stroke="var(--color-icon-cheek)" strokeWidth="1.3" strokeLinecap="round" opacity="0.75" />
    <line x1="9" y1="23" x2="17" y2="21.8" stroke="var(--color-icon-cheek)" strokeWidth="1.3" strokeLinecap="round" opacity="0.65" />
    {/* Page lines (right) */}
    <line x1="23" y1="13.8" x2="31" y2="15" stroke="var(--color-icon-cheek)" strokeWidth="1.3" strokeLinecap="round" opacity="0.85" />
    <line x1="23" y1="17.8" x2="31" y2="19" stroke="var(--color-icon-cheek)" strokeWidth="1.3" strokeLinecap="round" opacity="0.75" />
    <line x1="23" y1="21.8" x2="31" y2="23" stroke="var(--color-icon-cheek)" strokeWidth="1.3" strokeLinecap="round" opacity="0.65" />
  </>);
}

export function IconChart({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Baseline */}
    <line x1="4" y1="34" x2="36" y2="34" stroke="var(--color-icon-accent)" strokeWidth="1.6" strokeLinecap="round" />
    {/* Bar 1 (shortest) */}
    <rect x="6" y="22" width="6" height="12" rx="1.2" fill="var(--color-icon-body)" opacity="0.75" />
    {/* Bar 2 */}
    <rect x="14" y="14" width="6" height="20" rx="1.2" fill="var(--color-icon-body)" />
    {/* Bar 3 (tallest, Citrine — "trend high" / "today" warm signal) */}
    <rect x="22" y="8" width="6" height="26" rx="1.2" fill="var(--color-icon-warm-body)" />
    <rect x="23.5" y="11" width="3" height="20" rx="0.6" fill="var(--color-icon-warm-cheek)" opacity="0.7" />
    {/* Bar 4 */}
    <rect x="30" y="18" width="6" height="16" rx="1.2" fill="var(--color-icon-body)" opacity="0.6" />
  </>);
}

export function IconAlertSiren({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Base plate */}
    <rect x="9" y="29" width="22" height="6" rx="1.5" fill="var(--color-icon-body)" />
    {/* Dome (warm Citrine — alert urgency) */}
    <path d="M 11 29 Q 11 13 20 13 Q 29 13 29 29 Z" fill="var(--color-icon-warm-body)" />
    {/* Dome inner glow */}
    <path d="M 13.5 29 Q 13.5 15.5 20 15.5 Q 26.5 15.5 26.5 29 Z" fill="var(--color-icon-warm-cheek)" opacity="0.75" />
    {/* Light beams */}
    <path d="M 4 18 L 9 22" stroke="var(--color-icon-warm-body)" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M 36 18 L 31 22" stroke="var(--color-icon-warm-body)" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M 5 11 L 10 16" stroke="var(--color-icon-warm-body)" strokeWidth="1.4" strokeLinecap="round" opacity="0.7" />
    <path d="M 35 11 L 30 16" stroke="var(--color-icon-warm-body)" strokeWidth="1.4" strokeLinecap="round" opacity="0.7" />
    {/* Top finial */}
    <circle cx="20" cy="11" r="1.4" fill="var(--color-icon-accent)" />
  </>);
}

// ─── Top-30 batch 3 (2026-05-21 evening) ─────────────────────────────
// Four utility icons replacing 🤝 🧠 🔥 🩺.

export function IconHandshake({ size = 24, ariaLabel }: CrystalIconProps) {
  // Silhouette adapted from svgrepo handshake-svgrepo-com.svg (viewBox 0 -8 72 72).
  // Scaled to 40×40 via translate + scale(0.555). Pure amethyst — Citrine
  // dropped because the warmth here is the gesture itself, not a colour-coded
  // accent that just reads as a stain.
  return svg(size, ariaLabel, <g transform="translate(0, 4.4) scale(0.555)">
    <path d="M64,12.78v17s-3.63.71-4.38.81-3.08.85-4.78-.78C52.22,27.25,42.93,18,42.93,18a3.54,3.54,0,0,0-4.18-.21c-2.36,1.24-5.87,3.07-7.33,3.78a3.37,3.37,0,0,1-5.06-2.64,3.44,3.44,0,0,1,2.1-3c3.33-2,10.36-6,13.29-7.52,1.78-1,3.06-1,5.51,1C50.27,12,53,14.27,53,14.27a2.75,2.75,0,0,0,2.26.43C58.63,14,64,12.78,64,12.78ZM27,41.5a3,3,0,0,0-3.55-4.09,3.07,3.07,0,0,0-.64-3,3.13,3.13,0,0,0-3-.75,3.07,3.07,0,0,0-.65-3,3.38,3.38,0,0,0-4.72.13c-1.38,1.32-2.27,3.72-1,5.14s2.64.55,3.72.3c-.3,1.07-1.2,2.07-.09,3.47s2.64.55,3.72.3c-.3,1.07-1.16,2.16-.1,3.46s2.84.61,4,.25c-.45,1.15-1.41,2.39-.18,3.79s4.08.75,5.47-.58a3.32,3.32,0,0,0,.3-4.68A3.18,3.18,0,0,0,27,41.5Zm25.35-8.82L41.62,22a3.53,3.53,0,0,0-3.77-.68c-1.5.66-3.43,1.56-4.89,2.24a8.15,8.15,0,0,1-3.29,1.1,5.59,5.59,0,0,1-3-10.34C29,12.73,34.09,10,34.09,10a6.46,6.46,0,0,0-5-2C25.67,8,18.51,12.7,18.51,12.7a5.61,5.61,0,0,1-4.93.13L8,10.89v19.4s1.59.46,3,1a6.33,6.33,0,0,1,1.56-2.47,6.17,6.17,0,0,1,8.48-.06,5.4,5.4,0,0,1,1.34,2.37,5.49,5.49,0,0,1,2.29,1.4A5.4,5.4,0,0,1,26,34.94a5.47,5.47,0,0,1,3.71,4,5.38,5.38,0,0,1,2.39,1.43,5.65,5.65,0,0,1,1.48,4.89,0,0,0,0,1,0,0s.8.9,1.29,1.39a2.46,2.46,0,0,0,3.48-3.48s2,2.48,4.28,1c2-1.4,1.69-3.06.74-4a3.19,3.19,0,0,0,4.77.13,2.45,2.45,0,0,0,.13-3.3s1.33,1.81,4,.12c1.89-1.6,1-3.43,0-4.39Z" fill="var(--color-icon-body)" />
  </g>);
}

export function IconBrain({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Left hemisphere */}
    <path d="M 19 6 Q 11 6 9 12 Q 5 13 5 18 Q 5 22 8 24 Q 7 28 11 30 Q 14 33 19 32 Z" fill="var(--color-icon-body)" />
    {/* Right hemisphere */}
    <path d="M 21 6 Q 29 6 31 12 Q 35 13 35 18 Q 35 22 32 24 Q 33 28 29 30 Q 26 33 21 32 Z" fill="var(--color-icon-body)" />
    {/* Left hemisphere belly */}
    <path d="M 18 9 Q 13 9 11 13 Q 8 14 8 18 Q 8 21 10 22.5 Q 9.5 26 12.5 27.5 Q 15 29.5 18 29 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Right hemisphere belly */}
    <path d="M 22 9 Q 27 9 29 13 Q 32 14 32 18 Q 32 21 30 22.5 Q 30.5 26 27.5 27.5 Q 25 29.5 22 29 Z" fill="var(--color-icon-belly)" opacity="var(--color-icon-belly-opacity)" />
    {/* Folds — left */}
    <path d="M 11 14 Q 14 13 14 17 Q 14 19 12 19.5" stroke="var(--color-icon-accent)" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.55" />
    <path d="M 11 23 Q 14 22 14 26" stroke="var(--color-icon-accent)" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.55" />
    {/* Folds — right */}
    <path d="M 29 14 Q 26 13 26 17 Q 26 19 28 19.5" stroke="var(--color-icon-accent)" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.55" />
    <path d="M 29 23 Q 26 22 26 26" stroke="var(--color-icon-accent)" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.55" />
    {/* Central fissure */}
    <line x1="20" y1="6" x2="20" y2="32" stroke="var(--color-icon-accent)" strokeWidth="1.2" opacity="0.7" />
  </>);
}

export function IconFire({ size = 24, ariaLabel }: CrystalIconProps) {
  // Silhouette adapted from svgrepo fire-fill-1-svgrepo-com.svg (24×24 viewBox).
  // Scaled to 40×40 via scale(1.667). Outer = warm-body Citrine; inner
  // hot-spot heart = warm-cheek.
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <path d="M10.3671 4.06132C9.6671 4.56707 5 8.15805 5 13.9996C5 14.9188 5.18106 15.8291 5.53284 16.6784C5.88463 17.5277 6.40024 18.2993 7.05025 18.9493C7.70026 19.5993 8.47194 20.115 9.32122 20.4667C10.146 20.8084 11.0282 20.989 11.9203 20.9991C9.74796 20.9567 8 19.1824 8 16.9999C8 16.8024 8.01432 16.6082 8.04197 16.4184C8.04315 16.4071 8.04459 16.3957 8.04628 16.3843C8.3817 14.1305 10.4553 12.2171 11.4581 11.4101C11.7785 11.1523 12.2216 11.1523 12.5421 11.4101C13.5448 12.2171 15.6183 14.1305 15.9537 16.3843C15.9554 16.3957 15.9569 16.4071 15.958 16.4184C15.9857 16.6082 16 16.8024 16 16.9999C16 19.1824 14.252 20.9567 12.0797 20.9991C12.9718 20.989 13.854 20.8084 14.6788 20.4667C15.5281 20.115 16.2997 19.5993 16.9497 18.9493C17.5998 18.2993 18.1154 17.5277 18.4672 16.6784C18.807 15.8579 18.9875 14.9804 18.9994 14.093C18.9998 14.0815 19 14.07 19 14.0584L18.9999 14.0268L19 13.9997L18.9999 13.9996L18.9998 13.9995C18.9734 9.75884 16.1044 7.4446 15.5813 7.05781C15.5323 7.02155 15.4671 7.02196 15.4181 7.05823C15.2083 7.21337 14.6241 7.67639 13.9967 8.44729C13.9251 8.53521 13.7845 8.5184 13.7361 8.41591C12.5573 5.92135 10.9243 4.40726 10.5386 4.07102C10.4888 4.02764 10.4206 4.02266 10.3671 4.06132Z" fill="var(--color-icon-warm-body)" />
    <path d="M8.04628 16.3844C8.3817 14.1306 10.4553 12.2172 11.4581 11.4102C11.7785 11.1523 12.2216 11.1523 12.5421 11.4102C13.5448 12.2172 15.6183 14.1306 15.9537 16.3844C15.9554 16.3958 15.9569 16.4071 15.958 16.4184C15.9857 16.6083 16 16.8025 16 17C16 19.2091 14.2091 21 12 21C9.79086 21 8 19.2091 8 17C8 16.8024 8.01432 16.6083 8.04197 16.4184C8.04315 16.4071 8.04459 16.3958 8.04628 16.3844Z" fill="var(--color-icon-warm-cheek)" />
  </g>);
}

export function IconStethoscope({ size = 24, ariaLabel }: CrystalIconProps) {
  // Silhouette adapted from svgrepo stethoscope-svgrepo-com.svg (viewBox 0 -0.5 17 17).
  // Scaled to 40×40 via translate + scale(2.35).
  return svg(size, ariaLabel, <g transform="translate(0, 1.18) scale(2.35)">
    <path d="M16.938,10 C16.938,8.912 16.057,8.031 14.969,8.031 C13.881,8.031 13,8.912 13,10 C13,10.736 13.408,11.37 14.007,11.708 C13.449,13.619 12.06,14.687 10.434,14.687 C8.481,14.687 6.867,13.392 6.627,10.758 C8.45,10.625 10.958,9.509 10.958,7.203 L10.958,3.624 C10.958,2.767 10.602,1.967 9.969,1.436 C9.939,0.658 9.301,0.035 8.516,0.035 C7.71,0.035 7.057,0.688 7.057,1.494 C7.057,2.3 7.71,2.953 8.516,2.953 C8.814,2.953 9.09,2.862 9.322,2.71 C9.507,2.958 10.016,3.3 10.016,3.624 L10.016,7.203 C10.016,8.874 7.233,10.03 6.264,10.03 L5.467,10.03 C4.813,10.03 1.969,9.057 1.969,7.203 L1.969,3.624 C1.969,3.304 2.567,2.953 2.748,2.706 C2.977,2.854 3.248,2.942 3.541,2.942 C4.347,2.942 5,2.289 5,1.483 C5,0.677 4.347,0.024 3.541,0.024 C2.75,0.024 2.109,0.655 2.086,1.441 C1.464,1.97 1.016,2.774 1.016,3.624 L1.016,7.203 C1.016,9.559 3.381,10.672 5.223,10.763 C5.49,14.132 7.775,15.976 10.483,15.976 C12.778,15.976 14.682,14.466 15.379,11.924 C16.269,11.736 16.938,10.947 16.938,10 L16.938,10 Z" fill="var(--color-icon-body)" />
    {/* Warm Citrine highlight at the chest-piece centre — listens for warmth */}
    <circle cx="14.969" cy="10" r="0.85" fill="var(--color-icon-warm-body)" />
  </g>);
}

// ─── Wong-Baker FACES scale (2026-05-21 late) ─────────────────────────
// Six Citrine-palette face icons drawn for slider-scale legibility
// (22-32px). Pediatric accessibility-driven: each face must be instantly
// readable as a distinct distress level for low-literacy + ESL youth.
// Shared elements: amber body circle + lighter belly halo + dark-brown
// (Citrine eye token) features. Mouth + eye geometry exaggerated for
// small-size legibility. Not part of the CrystalEmoji posture system —
// these are static SVG glyphs.

// Citrine FACES colours are hardcoded (NOT token-driven) so the face reads
// the same across light/dark/high-contrast modes. The slider FACES is an
// accessibility surface for low-literacy + ESL pediatric users — the face
// expression must be instantly readable in any theme.
const FACE_BODY = '#f5c93a';      // saturated Citrine yellow
const FACE_FEATURE = '#181818';   // near-black for maximum contrast
const FACE_TEAR = '#2a7fc8';      // visible deep blue tear

function FaceBase({ children }: { children: JSX.Element | JSX.Element[] }) {
  return (
    <>
      <circle cx="20" cy="20" r="18" fill={FACE_BODY} />
      {children}
    </>
  );
}

export function IconFaces0({ size = 24, ariaLabel }: CrystalIconProps) {
  // FACES 0 — No hurt. Big arc smile + happy curved-up eyes.
  return svg(size, ariaLabel, <FaceBase>
    <path d="M9 17 Q12 12 15 17" stroke={FACE_FEATURE} strokeWidth="3" fill="none" strokeLinecap="round" />
    <path d="M25 17 Q28 12 31 17" stroke={FACE_FEATURE} strokeWidth="3" fill="none" strokeLinecap="round" />
    <path d="M10 23 Q20 33 30 23" stroke={FACE_FEATURE} strokeWidth="3.4" fill="none" strokeLinecap="round" />
  </FaceBase>);
}

export function IconFaces2({ size = 24, ariaLabel }: CrystalIconProps) {
  // FACES 2 — Hurts a little bit. Small smile + dot eyes.
  return svg(size, ariaLabel, <FaceBase>
    <circle cx="13" cy="17" r="2.6" fill={FACE_FEATURE} />
    <circle cx="27" cy="17" r="2.6" fill={FACE_FEATURE} />
    <path d="M12 24 Q20 30 28 24" stroke={FACE_FEATURE} strokeWidth="3.2" fill="none" strokeLinecap="round" />
  </FaceBase>);
}

export function IconFaces4({ size = 24, ariaLabel }: CrystalIconProps) {
  // FACES 4 — Hurts a little more. Neutral mouth rendered as a chunky pill
  // (not a stroke) so it carries visual weight at small sizes; thin lines
  // disappear in the face context.
  return svg(size, ariaLabel, <FaceBase>
    <circle cx="13" cy="17" r="2.6" fill={FACE_FEATURE} />
    <circle cx="27" cy="17" r="2.6" fill={FACE_FEATURE} />
    <rect x="11" y="24" width="18" height="4" rx="2" fill={FACE_FEATURE} />
  </FaceBase>);
}

export function IconFaces6({ size = 24, ariaLabel }: CrystalIconProps) {
  // FACES 6 — Hurts even more. Small frown + chunky dot eyes.
  return svg(size, ariaLabel, <FaceBase>
    <circle cx="13" cy="18" r="2.8" fill={FACE_FEATURE} />
    <circle cx="27" cy="18" r="2.8" fill={FACE_FEATURE} />
    <path d="M11 28 Q20 22 29 28" stroke={FACE_FEATURE} strokeWidth="3.4" fill="none" strokeLinecap="round" />
  </FaceBase>);
}

export function IconFaces8({ size = 24, ariaLabel }: CrystalIconProps) {
  // FACES 8 — Hurts a whole lot. Big frown + worried eyes + one tear.
  return svg(size, ariaLabel, <FaceBase>
    <circle cx="13" cy="18" r="2.8" fill={FACE_FEATURE} />
    <circle cx="27" cy="18" r="2.8" fill={FACE_FEATURE} />
    <path d="M10 29 Q20 19 30 29" stroke={FACE_FEATURE} strokeWidth="3.6" fill="none" strokeLinecap="round" />
    {/* Tear from outer corner of left eye */}
    <path d="M10 21 Q8 25 10 29 Q12 25 10 21 Z" fill={FACE_TEAR} />
  </FaceBase>);
}

export function IconFaces10({ size = 24, ariaLabel }: CrystalIconProps) {
  // FACES 10 — Hurts worst. Squinty crying eyes + open wailing mouth + big tear.
  return svg(size, ariaLabel, <FaceBase>
    {/* Squinty closed eyes (down-curve = scrunched) */}
    <path d="M9 18 Q13 21 17 18" stroke={FACE_FEATURE} strokeWidth="3" fill="none" strokeLinecap="round" />
    <path d="M23 18 Q27 21 31 18" stroke={FACE_FEATURE} strokeWidth="3" fill="none" strokeLinecap="round" />
    {/* Open wailing mouth */}
    <ellipse cx="20" cy="28" rx="4.5" ry="4" fill={FACE_FEATURE} />
    {/* Big tear streaming */}
    <path d="M9 22 Q7 27 9 32 Q12 33 12.5 31 Q13 26 11 22 Z" fill={FACE_TEAR} />
  </FaceBase>);
}

// ─── Bottom-nav family (Wave 3.5 Track A, 2026-05-22) ───
// Three icons that replace the placeholder bottom-nav glyphs (`journey-moving-out`
// suitcase / `checklist` clipboard / `solidarity` love-hand). Designed as a
// coherent family alongside IconChart (Patterns) + IconGrowthSprout (Growth):
// amethyst body with a single Citrine warm focal per icon.

export function IconHome({ size = 24, ariaLabel }: CrystalIconProps) {
  // Silhouette adapted from svgrepo home-house (32×32 viewBox), scaled to fit
  // 40×40 via scale(1.25). The original path carves an arched doorway as a
  // cutout — a separate Citrine warm-body layer sits behind so the door reads
  // as warm-lit. Tiny cream doorknob anchors the Citrine focal.
  return svg(size, ariaLabel, <g transform="scale(1.25)">
    {/* Citrine door behind — peeks through the arched cutout in the house path */}
    <path d="M11 23 C11 20.243 13.243 18 16 18 C18.757 18 21 20.243 21 23 L21 30 L11 30 Z" fill="var(--color-icon-warm-body)" />
    {/* House silhouette in amethyst — door cutout reveals the Citrine behind */}
    <path d="M30.854,16.548C30.523,17.43,29.703,18,28.764,18H28v11c0,0.552-0.448,1-1,1h-6v-7c0-2.757-2.243-5-5-5s-5,2.243-5,5v7H5c-0.552,0-1-0.448-1-1V18H3.235c-0.939,0-1.759-0.569-2.09-1.451c-0.331-0.882-0.088-1.852,0.62-2.47L13.444,3.019c1.434-1.357,3.679-1.357,5.112,0l11.707,11.086C30.941,14.696,31.185,15.666,30.854,16.548z" fill="var(--color-icon-body)" />
    {/* Doorknob — Citrine cream highlight at right-middle of door */}
    <circle cx="19.5" cy="24" r="0.5" fill="var(--color-icon-warm-cheek)" />
  </g>);
}

export function IconTools({ size = 24, ariaLabel }: CrystalIconProps) {
  // Silhouette adapted from svgrepo tools (72×72 viewBox starting at y=-8),
  // scaled to fit 40×40 via translate(0, 8) + scale(0.556). Two crossed tools
  // — wrench at bottom-left + screwdriver at top-right. Screwdriver tip
  // picked out in Citrine warm-body as the family-pattern focal moment.
  return svg(size, ariaLabel, <g transform="translate(0, 4.4) scale(0.556)">
    {/* Wrench head — amethyst */}
    <path d="M13.32,44.31a4.5,4.5,0,0,0,6.36,6.36L34.44,35.91l-6.36-6.36Z" fill="var(--color-icon-body)" />
    {/* Screwdriver tip — Citrine warm focal */}
    <polygon points="50.2 16.96 56.63 13.63 60 7.13 56.86 3.99 50.36 7.36 47.03 13.78 39.21 21.61 42.38 24.79 50.2 16.96" fill="var(--color-icon-warm-body)" />
    {/* Connecting bodies + grips — amethyst */}
    <path d="M51.61,34,51,34a8.9,8.9,0,0,0-3.11.58L29.45,16.09A8.9,8.9,0,0,0,30,13l-.06-.6A8.9,8.9,0,0,0,17.19,4.89l6,6a3,3,0,0,1,.68,1.08A3,3,0,0,1,21,16a2.92,2.92,0,0,1-1-.21,3.15,3.15,0,0,1-1.08-.67l-6-6a8.9,8.9,0,0,0,7.49,12.78L21,22a8.9,8.9,0,0,0,3.11-.58L42.6,39.84A8.9,8.9,0,0,0,42,43l.06.6A9,9,0,0,0,51,51.94a8.72,8.72,0,0,0,3.85-.9l-6-6A3.08,3.08,0,0,1,48.21,44,3,3,0,0,1,51,40a2.92,2.92,0,0,1,1,.21,2.72,2.72,0,0,1,1.08.67l6,6A8.9,8.9,0,0,0,51.61,34Z" fill="var(--color-icon-body)" />
  </g>);
}

export function IconCircle({ size = 24, ariaLabel }: CrystalIconProps) {
  // Six "people" dots arranged in a circle on a dashed connecting ring.
  // Top dot is Citrine warm-body (the user); flanking five are deep amethyst
  // (the community/peers). Reads as both literal "circle" and "community of
  // people" at small nav sizes. Selected for the bottom-nav Circle tab over
  // the prior IconSolidarity (love-hand making a heart, which read as
  // "advocacy" rather than "community").
  return svg(size, ariaLabel, <>
    {/* Dashed ring — the connecting weave between members */}
    <circle cx="20" cy="20" r="14" stroke="var(--color-icon-accent)" strokeWidth="2" fill="none" strokeDasharray="1.5 2" opacity="0.5" />
    {/* Top — Citrine focal (you) */}
    <circle cx="20" cy="6" r="3.5" fill="var(--color-icon-warm-body)" />
    {/* Five peers around the ring — deep amethyst */}
    <circle cx="32" cy="13" r="3.5" fill="var(--color-icon-body)" />
    <circle cx="32" cy="27" r="3.5" fill="var(--color-icon-body)" />
    <circle cx="20" cy="34" r="3.5" fill="var(--color-icon-body)" />
    <circle cx="8" cy="27" r="3.5" fill="var(--color-icon-body)" />
    <circle cx="8" cy="13" r="3.5" fill="var(--color-icon-body)" />
  </>);
}

// ─── Mood chip icons (Phase 3 Track C, 2026-05-24) ───
// β.1 geometric-abstract family for the FEELING_CHIPS mood selector. Source
// SVGs drawn at 24×24 viewBox; scaled to the canonical 40×40 wrapper via
// `transform="scale(1.667)"`. Use currentColor (NOT amethyst tokens) so the
// chip-pill `color: var(--color-accent)` propagates and the icons match the
// surrounding chip text colour at 14 px chip-scale.
//
// Geometric signatures across the family (β.1 picks):
// - mood-low      → weighted disc + faint top line  (weight bias ↓)
// - mood-anxious  → full-width zigzag
// - mood-calm     → stable horizon + center anchor
// - mood-hopeful  → light point + halo + ground line  (Low inverse)
// - mood-warrior  → planted vertical bar + base line
// - mood-lonely   → small dot inside large empty circle

export function IconMoodLow({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <circle cx="12" cy="16" r="5" fill="currentColor"/>
    <line x1="6" y1="6" x2="18" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.4"/>
  </g>);
}

export function IconMoodAnxious({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <path d="M3 12 L 7 7 L 10 15 L 13 6 L 16 16 L 19 9 L 21 12" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
  </g>);
}

export function IconMoodCalm({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
    <circle cx="12" cy="12" r="2.5" fill="currentColor"/>
  </g>);
}

export function IconMoodHopeful({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <circle cx="12" cy="8" r="3.2" fill="currentColor"/>
    <circle cx="12" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.2" fill="none" opacity="0.35"/>
    <line x1="6" y1="18" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.4"/>
  </g>);
}

export function IconMoodWarrior({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <rect x="10.5" y="4" width="3" height="14" rx="0.5" fill="currentColor"/>
    <line x1="6" y1="19.5" x2="18" y2="19.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
  </g>);
}

export function IconMoodLonely({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <circle cx="12" cy="12" r="2.2" fill="currentColor"/>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.4" fill="none" opacity="0.35"/>
  </g>);
}

// ─── Treatment-rating chip icons (Phase 3 Track B, added 2026-05-24) ───
// β.1 geometric-abstract family, recoloured via graduated amethyst
// (--color-crisis-stage-{1..4}). Use currentColor so the chip-card consumer
// can set `color: var(--color-crisis-stage-N)` on the wrapping element and
// the icon adopts the depth-graduated palette. Same equity-respecting
// risk-hierarchy decision as Crisis Plan stages, Weather Risk, ED triage,
// body-map intensity — no traffic-light read possible since every chip is
// one hue at different depths; geometry carries the contextual meaning.
//
// Geometric signatures (shared with mood family where possible):
// - treatment-great → rising-light + halo + ground line (reuses Hopeful)
// - treatment-good  → parallel-marks (NEW; avoids Calm collision)
// - treatment-mixed → zigzag (reuses Anxious)
// - treatment-poor  → weighted disc + faint top line (reuses Low)

export function IconTreatmentGreat({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <circle cx="12" cy="8" r="3.2" fill="currentColor"/>
    <circle cx="12" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.2" fill="none" opacity="0.35"/>
    <line x1="6" y1="18" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.4"/>
  </g>);
}

export function IconTreatmentGood({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <line x1="5" y1="9" x2="19" y2="9" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
    <line x1="5" y1="15" x2="19" y2="15" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" opacity="0.65"/>
  </g>);
}

export function IconTreatmentMixed({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <path d="M3 12 L 7 7 L 10 15 L 13 6 L 16 16 L 19 9 L 21 12" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
  </g>);
}

export function IconTreatmentPoor({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(1.667)">
    <circle cx="12" cy="16" r="5" fill="currentColor"/>
    <line x1="6" y1="6" x2="18" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.4"/>
  </g>);
}

// ─── Medication frequency icons (Phase 3 Track E, added 2026-05-24) ───
// β picks across the family — see preview HTML at
// ~/Documents/PhD/Working Documents/CareWeave/icon-system-extension/2026-05-24/
// medication-frequency-round-1-preview_v1.html
// Citrine via --color-icon-warm-body (canonical token) where literal:
//   - once-daily sun-center: morning warmth
//   - twice-daily day-half: AM warmth vs PM amethyst
//   - as-needed clapper: alert / attention warmth
//   - weekly date: "this day" highlight
// All-amethyst (currentColor) elsewhere; thrice-daily and alt-days carry no
// Citrine because warmth isn't doing real meaning there.

export function IconMedFreqOnceDaily({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    <circle cx="20" cy="20" r="6" fill="var(--color-icon-warm-body)"/>
    <line x1="20" y1="6" x2="20" y2="11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="34" y1="20" x2="29" y2="20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="20" y1="34" x2="20" y2="29" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="6" y1="20" x2="11" y2="20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </>);
}

export function IconMedFreqTwiceDaily({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Sun half (Citrine = AM warmth) */}
    <path d="M 20 10 A 10 10 0 0 0 20 30 Z" fill="var(--color-icon-warm-body)"/>
    {/* Moon half (amethyst = PM) */}
    <path d="M 20 10 A 10 10 0 0 1 20 30 Z" fill="currentColor"/>
    {/* Faint full-circle ring binds the two halves visually */}
    <circle cx="20" cy="20" r="10" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.4"/>
  </>);
}

export function IconMedFreqThriceDaily({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    <circle cx="20" cy="20" r="11" fill="none" stroke="currentColor" strokeWidth="1.8"/>
    {/* 3 dose-marks at clock positions 12 / 4 / 8 — morning / noon / evening */}
    <circle cx="20" cy="11.5" r="2" fill="currentColor"/>
    <circle cx="27.4" cy="24.25" r="2" fill="currentColor"/>
    <circle cx="12.6" cy="24.25" r="2" fill="currentColor"/>
    <circle cx="20" cy="20" r="1.2" fill="currentColor"/>
  </>);
}

export function IconMedFreqAsNeeded({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Bell silhouette */}
    <path d="M 20 9 C 14.5 9 12 13.5 12 19 L 12 25 L 10 28 L 30 28 L 28 25 L 28 19 C 28 13.5 25.5 9 20 9 Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
    {/* Bell top stem */}
    <line x1="20" y1="6.5" x2="20" y2="9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    {/* Citrine clapper = alert / attention focal */}
    <circle cx="20" cy="31.5" r="2.2" fill="var(--color-icon-warm-body)"/>
  </>);
}

export function IconMedFreqWeekly({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    <rect x="7" y="11" width="26" height="22" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8"/>
    <line x1="7" y1="17.5" x2="33" y2="17.5" stroke="currentColor" strokeWidth="1.5"/>
    {/* Ring tabs */}
    <line x1="14" y1="8" x2="14" y2="12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="26" y1="8" x2="26" y2="12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    {/* Citrine highlighted date = "this day" */}
    <rect x="17.25" y="23" width="5.5" height="5.5" rx="0.8" fill="var(--color-icon-warm-body)"/>
  </>);
}

export function IconMedFreqAltDays({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Dose day → skip day → dose day pattern (literal alternation) */}
    <circle cx="9" cy="20" r="3.2" fill="currentColor"/>
    <circle cx="20" cy="20" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2" opacity="0.55"/>
    <circle cx="31" cy="20" r="3.2" fill="currentColor"/>
    <line x1="12.5" y1="20" x2="17" y2="20" stroke="currentColor" strokeWidth="1.2" opacity="0.35"/>
    <line x1="23" y1="20" x2="27.5" y2="20" stroke="currentColor" strokeWidth="1.2" opacity="0.35"/>
  </>);
}

// ─── Per-level badges (Phase 3 Track G, added 2026-05-24) ───
// Each badge is the CareWeave logo (careweave-net-icon.svg) partially built.
// Source paths use 60×60 viewBox (matching the logo file); wrapped in
// scale(0.6667) so they render inside the shared 40×40 svg() helper.
//
// Guide colours are hard-coded hex because each Crystal Guide IS a specific
// colour — the logo already decided which guide sits at which node:
//   #5a4080 Amethyst · #c07088 Rose Quartz · #c89838 Citrine · #285888 Sapphire
//   #408868 Jade · #222238 Onyx · #c0b098 Tiger Eye · #906828 brown
//   #8898b0 Moonstone · #181828 Obsidian · #d8a0b2 Pearl · #58a880 Jade-variant
//   #7858a8 center purple
// Net structural lines use currentColor with opacity; Citrine halo at L7
// uses --color-icon-warm-body.

export function IconLevel1({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(0.6667)">
    <circle cx="30" cy="30" r="22" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.25"/>
    <line x1="30" y1="8" x2="30" y2="52" stroke="currentColor" strokeWidth="1.4" opacity="0.85"/>
    <circle cx="30" cy="8" r="3" fill="#5a4080"/>
    <circle cx="30" cy="30" r="2.5" fill="currentColor" opacity="0.75"/>
  </g>);
}

export function IconLevel2({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(0.6667)">
    <circle cx="30" cy="30" r="22" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.4"/>
    <line x1="30" y1="8" x2="30" y2="52" stroke="currentColor" strokeWidth="1.2" opacity="0.8"/>
    <line x1="11" y1="19" x2="49" y2="41" stroke="currentColor" strokeWidth="1.2" opacity="0.8"/>
    <line x1="11" y1="41" x2="49" y2="19" stroke="currentColor" strokeWidth="1.2" opacity="0.8"/>
    <circle cx="30" cy="8" r="2.8" fill="#5a4080"/>
    <circle cx="49" cy="19" r="2.5" fill="#c07088"/>
    <circle cx="30" cy="30" r="2.5" fill="currentColor" opacity="0.75"/>
  </g>);
}

export function IconLevel3({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(0.6667)">
    <circle cx="30" cy="30" r="22" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.55"/>
    <line x1="30" y1="8" x2="30" y2="52" stroke="currentColor" strokeWidth="1.1" opacity="0.75"/>
    <line x1="11" y1="19" x2="49" y2="41" stroke="currentColor" strokeWidth="1.1" opacity="0.75"/>
    <line x1="11" y1="41" x2="49" y2="19" stroke="currentColor" strokeWidth="1.1" opacity="0.75"/>
    <path d="M30 8 Q40 14 49 19" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.6"/>
    <path d="M49 19 Q52 30 49 41" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.6"/>
    <circle cx="30" cy="8" r="2.8" fill="#5a4080"/>
    <circle cx="49" cy="19" r="2.5" fill="#c07088"/>
    {/* First Citrine — warmth arrives */}
    <circle cx="49" cy="41" r="2.5" fill="#c89838"/>
    <circle cx="30" cy="30" r="2.5" fill="currentColor" opacity="0.75"/>
  </g>);
}

export function IconLevel4({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(0.6667)">
    <circle cx="30" cy="30" r="22" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.65"/>
    <line x1="30" y1="8" x2="30" y2="52" stroke="currentColor" strokeWidth="1" opacity="0.7"/>
    <line x1="11" y1="19" x2="49" y2="41" stroke="currentColor" strokeWidth="1" opacity="0.7"/>
    <line x1="11" y1="41" x2="49" y2="19" stroke="currentColor" strokeWidth="1" opacity="0.7"/>
    <path d="M30 8 Q40 14 49 19" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.65"/>
    <path d="M49 19 Q52 30 49 41" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.65"/>
    <path d="M49 41 Q40 46 30 52" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.65"/>
    <path d="M11 19 Q20 14 30 8" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.65"/>
    <circle cx="30" cy="8" r="2.7" fill="#5a4080"/>
    <circle cx="49" cy="19" r="2.4" fill="#c07088"/>
    <circle cx="49" cy="41" r="2.4" fill="#c89838"/>
    <circle cx="30" cy="52" r="2.7" fill="#285888"/>
    <circle cx="11" cy="19" r="2.4" fill="#222238"/>
    <circle cx="30" cy="30" r="2.5" fill="currentColor" opacity="0.75"/>
  </g>);
}

export function IconLevel5({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(0.6667)">
    <circle cx="30" cy="30" r="22" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.75"/>
    <line x1="30" y1="8" x2="30" y2="52" stroke="currentColor" strokeWidth="0.7" opacity="0.55"/>
    <line x1="11" y1="19" x2="49" y2="41" stroke="currentColor" strokeWidth="0.7" opacity="0.55"/>
    <line x1="11" y1="41" x2="49" y2="19" stroke="currentColor" strokeWidth="0.7" opacity="0.55"/>
    <path d="M30 8 Q40 14 49 19" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.65"/>
    <path d="M49 19 Q52 30 49 41" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.65"/>
    <path d="M49 41 Q40 46 30 52" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.65"/>
    <path d="M30 52 Q20 46 11 41" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.65"/>
    <path d="M11 41 Q8 30 11 19" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.65"/>
    <path d="M11 19 Q20 14 30 8" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.65"/>
    <circle cx="30" cy="30" r="13" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.4"/>
    <circle cx="30" cy="8" r="2.7" fill="#5a4080"/>
    <circle cx="49" cy="19" r="2.4" fill="#c07088"/>
    <circle cx="49" cy="41" r="2.4" fill="#c89838"/>
    <circle cx="30" cy="52" r="2.7" fill="#285888"/>
    <circle cx="11" cy="41" r="2.4" fill="#408868"/>
    <circle cx="11" cy="19" r="2.4" fill="#222238"/>
    <circle cx="30" cy="30" r="2.5" fill="#7858a8"/>
  </g>);
}

export function IconLevel6({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(0.6667)">
    <circle cx="30" cy="30" r="22" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.85"/>
    <line x1="30" y1="8" x2="30" y2="52" stroke="currentColor" strokeWidth="0.7" opacity="0.55"/>
    <line x1="11" y1="19" x2="49" y2="41" stroke="currentColor" strokeWidth="0.7" opacity="0.55"/>
    <line x1="11" y1="41" x2="49" y2="19" stroke="currentColor" strokeWidth="0.7" opacity="0.55"/>
    <path d="M30 8 Q40 14 49 19" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.7"/>
    <path d="M49 19 Q52 30 49 41" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.7"/>
    <path d="M49 41 Q40 46 30 52" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.7"/>
    <path d="M30 52 Q20 46 11 41" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.7"/>
    <path d="M11 41 Q8 30 11 19" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.7"/>
    <path d="M11 19 Q20 14 30 8" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.7"/>
    <circle cx="30" cy="30" r="13" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.6"/>
    <path d="M30 17 Q36 20 39 24" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.6"/>
    <path d="M39 24 Q40 30 39 36" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.6"/>
    <path d="M39 36 Q36 40 30 43" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.6"/>
    <path d="M30 43 Q24 40 21 36" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.6"/>
    <circle cx="30" cy="8" r="2.7" fill="#5a4080"/>
    <circle cx="49" cy="19" r="2.4" fill="#c07088"/>
    <circle cx="49" cy="41" r="2.4" fill="#c89838"/>
    <circle cx="30" cy="52" r="2.7" fill="#285888"/>
    <circle cx="11" cy="41" r="2.4" fill="#408868"/>
    <circle cx="11" cy="19" r="2.4" fill="#222238"/>
    <circle cx="30" cy="17" r="2" fill="#c0b098"/>
    <circle cx="39" cy="24" r="2" fill="#906828"/>
    <circle cx="39" cy="36" r="2" fill="#8898b0"/>
    <circle cx="30" cy="30" r="2.5" fill="#7858a8"/>
  </g>);
}

export function IconLevel7({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <g transform="scale(0.6667)">
    {/* Citrine halo — illuminated finale */}
    <circle cx="30" cy="30" r="27" fill="var(--color-icon-warm-body)" opacity="0.18"/>
    <circle cx="30" cy="30" r="22" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.85"/>
    <line x1="30" y1="8" x2="30" y2="52" stroke="currentColor" strokeWidth="0.7" opacity="0.6"/>
    <line x1="11" y1="19" x2="49" y2="41" stroke="currentColor" strokeWidth="0.7" opacity="0.6"/>
    <line x1="11" y1="41" x2="49" y2="19" stroke="currentColor" strokeWidth="0.7" opacity="0.6"/>
    <circle cx="30" cy="30" r="13" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.65"/>
    <path d="M30 8 Q40 14 49 19" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.7"/>
    <path d="M49 19 Q52 30 49 41" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.7"/>
    <path d="M49 41 Q40 46 30 52" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.7"/>
    <path d="M30 52 Q20 46 11 41" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.7"/>
    <path d="M11 41 Q8 30 11 19" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.7"/>
    <path d="M11 19 Q20 14 30 8" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.7"/>
    <path d="M30 17 Q36 20 39 24" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.65"/>
    <path d="M39 24 Q40 30 39 36" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.65"/>
    <path d="M39 36 Q36 40 30 43" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.65"/>
    <path d="M30 43 Q24 40 21 36" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.65"/>
    <path d="M21 36 Q20 30 21 24" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.65"/>
    <path d="M21 24 Q24 20 30 17" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.65"/>
    <circle cx="30" cy="8" r="2.8" fill="#5a4080"/>
    <circle cx="49" cy="19" r="2.5" fill="#c07088"/>
    <circle cx="49" cy="41" r="2.5" fill="#c89838"/>
    <circle cx="30" cy="52" r="2.8" fill="#285888"/>
    <circle cx="11" cy="41" r="2.5" fill="#408868"/>
    <circle cx="11" cy="19" r="2.5" fill="#222238"/>
    <circle cx="30" cy="17" r="2" fill="#c0b098"/>
    <circle cx="39" cy="24" r="2" fill="#906828"/>
    <circle cx="39" cy="36" r="2" fill="#8898b0"/>
    <circle cx="30" cy="43" r="2" fill="#181828"/>
    <circle cx="21" cy="36" r="2" fill="#d8a0b2"/>
    <circle cx="21" cy="24" r="2" fill="#58a880"/>
    <circle cx="30" cy="30" r="2.5" fill="#7858a8"/>
  </g>);
}

// ─── Track G utility icons (Phase 3.5, added 2026-05-24) ───
// All 40×40 viewBox native (no scale wrapper). Pure amethyst (currentColor)
// except where Citrine carries real meaning: star-filled (favourited =
// warmth) and cake candle flame (literal warmth). The sickle is pure
// amethyst — SCD identity, not warmth.

export function IconPencil({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Diagonal pencil body */}
    <path d="M 26 6 L 34 14 L 14 34 L 6 34 L 6 26 Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
    {/* Ferrule line */}
    <line x1="22" y1="10" x2="30" y2="18" stroke="currentColor" strokeWidth="1.8"/>
    {/* Tip mark */}
    <line x1="6" y1="34" x2="11" y2="29" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </>);
}

export function IconGem({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Brilliant-cut gem (octagonal table + facets) */}
    <path d="M 10 16 L 14 8 L 26 8 L 30 16 L 20 33 Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
    <line x1="14" y1="8" x2="20" y2="16" stroke="currentColor" strokeWidth="1.6"/>
    <line x1="26" y1="8" x2="20" y2="16" stroke="currentColor" strokeWidth="1.6"/>
    <line x1="10" y1="16" x2="30" y2="16" stroke="currentColor" strokeWidth="1.6"/>
    <line x1="10" y1="16" x2="20" y2="33" stroke="currentColor" strokeWidth="1.6"/>
    <line x1="30" y1="16" x2="20" y2="33" stroke="currentColor" strokeWidth="1.6"/>
  </>);
}

export function IconCake({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Cake plate */}
    <line x1="5" y1="34" x2="35" y2="34" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    {/* Cake body */}
    <rect x="9" y="20" width="22" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8"/>
    {/* Frosting drip */}
    <path d="M 9 22 Q 13 25 17 22 Q 21 25 25 22 Q 29 25 31 22" fill="none" stroke="currentColor" strokeWidth="1.6"/>
    {/* Candle */}
    <line x1="20" y1="20" x2="20" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    {/* Citrine flame (literal warmth) */}
    <path d="M 20 6 Q 17.5 10 20 13 Q 22.5 10 20 6 Z" fill="var(--color-icon-warm-body)"/>
  </>);
}

export function IconDocument({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Page with folded corner */}
    <path d="M 10 6 L 26 6 L 32 12 L 32 34 L 10 34 Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
    <path d="M 26 6 L 26 12 L 32 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
    {/* Text lines */}
    <line x1="14" y1="18" x2="28" y2="18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    <line x1="14" y1="23" x2="28" y2="23" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    <line x1="14" y1="28" x2="22" y2="28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
  </>);
}

export function IconEnvelope({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    <rect x="5" y="10" width="30" height="20" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8"/>
    {/* Envelope flap */}
    <path d="M 5 12 L 20 22 L 35 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
  </>);
}

export function IconStarFilled({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* 5-point star, Citrine fill = favourited warmth */}
    <path d="M 20 5 L 24.5 15.5 L 36 17 L 27.5 24.5 L 30 36 L 20 30.5 L 10 36 L 12.5 24.5 L 4 17 L 15.5 15.5 Z" fill="var(--color-icon-warm-body)"/>
  </>);
}

export function IconStarOutlined({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    <path d="M 20 5 L 24.5 15.5 L 36 17 L 27.5 24.5 L 30 36 L 20 30.5 L 10 36 L 12.5 24.5 L 4 17 L 15.5 15.5 Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
  </>);
}

export function IconTraitSickle({ size = 24, ariaLabel }: CrystalIconProps) {
  return svg(size, ariaLabel, <>
    {/* Tilted asymmetric crescent — sickled cell shape. SCD community's
        claimed iconography. Tilt + elongation distinguish from celestial
        half-moon (med-freq-twice-daily). */}
    <g transform="rotate(-25 20 20)">
      <path d="M 12 8 A 13 13 0 1 0 28 32 A 9 13 0 1 1 12 8 Z" fill="currentColor"/>
    </g>
  </>);
}

